import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Users, Calendar, Image, Mail, Phone, MapPin, Briefcase, MessageSquare, Clock, CheckCircle, AlertCircle, ExternalLink, Search, Filter, BarChart3, X, Trash2, Shield, History, DollarSign, RefreshCw, UserCheck, UserCog, Download, FileText, Plus, Home, Bath, BedDouble, Sofa, ChefHat, PawPrint, Bell, TrendingUp, Lock, Eye, EyeOff, Edit, Star } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import AdminGalleryManagement from '../components/AdminGalleryManagement';
import AdminStats from '../components/AdminStats';
import AdminUserManagement from '../components/AdminUserManagement';
import AdminAuditLog from '../components/AdminAuditLog';
import AdminPricingManagement from '../components/AdminPricingManagement';
import AdminStaffManagement from '../components/AdminStaffManagement';
import AdminHoursReport from '../components/AdminHoursReport';
import AdminWhatsApp from '../components/AdminWhatsApp';
import AdminAirbnbGroups from '../components/AdminAirbnbGroups';
import FinanceAirbnbReceivables from '../components/FinanceAirbnbReceivables';
import AdminInvoicesHistory from '../components/AdminInvoicesHistory';
import AdminMonthCalendar from '../components/AdminMonthCalendar';
import MultiDatePicker from '../components/MultiDatePicker';
import ClientSearch from '../components/ClientSearch';
import ClientProfileModal from '../components/ClientProfileModal';
import AdminClientsHistory from '../components/AdminClientsHistory';
import CompletedServiceEditModal from '../components/CompletedServiceEditModal';
import AdminGoogleReviews from '../components/AdminGoogleReviews';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const num = (v, d = 0) => { const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.').match(/\d+(\.\d+)?/)?.[0]); return Number.isFinite(n) ? n : d; };

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout, token, user, isSuperAdmin } = useAdmin();
  const { language } = useLanguage();

  const [activeTab, setActiveTab] = useState('stats');
  const [contacts, setContacts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workSessions, setWorkSessions] = useState([]); // Track work sessions for completed jobs
  
  // Finance tab state
  const [financeUnlocked, setFinanceUnlocked] = useState(false);
  const [financePassword, setFinancePassword] = useState('');
  const [financePasswordError, setFinancePasswordError] = useState('');
  const [financePeriod, setFinancePeriod] = useState(7); // 7, 15, or 30 days
  const [showChangePinModal, setShowChangePinModal] = useState(false);
  const [currentPinInput, setCurrentPinInput] = useState('');
  const [newPinInput, setNewPinInput] = useState('');
  const [confirmPinInput, setConfirmPinInput] = useState('');
  const [pinChangeError, setPinChangeError] = useState('');
  const [pinChangeSuccess, setPinChangeSuccess] = useState('');
  const [changingPin, setChangingPin] = useState(false);
  
  // Finance expenses state
  const [expenses, setExpenses] = useState([]);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    name: '',
    amount: '',
    expense_type: 'fixed',
    category: 'other',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    recurring: false
  });
  const [savingExpense, setSavingExpense] = useState(false);
  const [showEditServiceModal, setShowEditServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceFinanceForm, setServiceFinanceForm] = useState({
    estimated_price: '',
    staff_cost: '',
    notes: ''
  });
  const [savingServiceFinance, setSavingServiceFinance] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [useCustomDates, setUseCustomDates] = useState(false);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [staff, setStaff] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  
  // Delete confirmation modal
  const [deleteModal, setDeleteModal] = useState({ show: false, type: '', id: '', name: '' });
  const [refreshing, setRefreshing] = useState(false);
  const [imageModal, setImageModal] = useState({ show: false, url: '', title: '' });
  
  // Manual appointment creation modal
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null); // For edit mode
  const [newAppointment, setNewAppointment] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    eircode: '',
    service_type: 'regular',
    custom_service_name: '',
    custom_service_price: '',
    property_type: 'apartment',
    bedrooms: 2,
    bathrooms: 1,
    living_rooms: 1,
    has_kitchen: true,
    has_pets: false,
    preferred_date: '',
    preferred_time: '09:00',
    special_requests: '',
    notes: '',
    discount_type: 'none',
    discount_value: '',
    airbnb_price: '',
    airbnb_weekdays: [],
    airbnb_weeks: 4,
    airbnb_mode: 'weekdays',
    airbnb_dates: []
  });
  const [airbnbSettings, setAirbnbSettings] = useState({ price: 60, time: '11:00', service_name: 'Airbnb Cleaning' });
  const [showAirbnbGroups, setShowAirbnbGroups] = useState(false);
  const [calendarView, setCalendarView] = useState('month');
  const [profileClient, setProfileClient] = useState(null);
  const [editingCompleted, setEditingCompleted] = useState(null);
  const [airbnbGroupsKey, setAirbnbGroupsKey] = useState(0);
  const [appointmentEstimate, setAppointmentEstimate] = useState({ price: 0, time: '' });
  const [creatingAppointment, setCreatingAppointment] = useState(false);

  // Calculate estimate for manual appointment
  const calculateManualEstimate = useCallback((data) => {
    // If custom service, use custom price
    const applyDiscount = (base, time, totalHours) => {
      const value = parseFloat(data.discount_value) || 0;
      let discount = 0;
      if (data.discount_type === 'percent') discount = base * Math.min(value, 100) / 100;
      else if (data.discount_type === 'fixed') discount = Math.min(value, base);
      discount = Math.round(discount * 100) / 100;
      return { price: Math.round((base - discount) * 100) / 100, originalPrice: base, discount, time, totalHours };
    };
    if (data.service_type === 'airbnb') {
      return applyDiscount(parseFloat(data.airbnb_price) || 0, 'Airbnb', 0);
    }
    if (data.service_type === 'custom') {
      return applyDiscount(parseFloat(data.custom_service_price) || 0, 'Custom', 0);
    }
    
    const serviceType = data.service_type;
    const isDeep = serviceType === 'deep' || serviceType === 'endoftenancy';
    const hourlyRate = isDeep ? 31 : 23; // €31/h deep, €23/h regular
    
    // Time estimates in hours
    const timeEstimates = isDeep ? {
      bedroom: 1,
      bathroom: 1.5,
      living_room: 1,
      kitchen: 1.5
    } : {
      bedroom: 0.5,
      bathroom: 0.75,
      living_room: 0.5,
      kitchen: 0.75
    };
    
    let totalHours = 0;
    totalHours += (parseInt(data.bedrooms) || 0) * timeEstimates.bedroom;
    totalHours += (parseInt(data.bathrooms) || 0) * timeEstimates.bathroom;
    totalHours += (parseInt(data.living_rooms) || 0) * timeEstimates.living_room;
    if (data.has_kitchen) totalHours += timeEstimates.kitchen;
    
    // Minimum 2 hours
    totalHours = Math.max(totalHours, 2);
    
    const price = totalHours * hourlyRate;
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);
    const timeStr = minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    
    return applyDiscount(Math.round(price * 100) / 100, timeStr, totalHours);
  }, []);
  
  // Update estimate when form changes
  useEffect(() => {
    const estimate = calculateManualEstimate(newAppointment);
    setAppointmentEstimate(estimate);
  }, [newAppointment, calculateManualEstimate]);

  // Pending WhatsApp review reminders count
  const [reminderCount, setReminderCount] = useState(0);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/reminders/pending`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setReminderCount(r.data.count))
      .catch(() => {});
    axios.get(`${API}/admin/airbnb-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAirbnbSettings(r.data))
      .catch(() => {});
  }, [token]);

  const saveAirbnbDefaults = async () => {
    const price = parseFloat(newAppointment.airbnb_price);
    if (!price) return;
    const { data } = await axios.put(`${API}/admin/airbnb-settings`, { price, time: newAppointment.preferred_time }, { headers: { Authorization: `Bearer ${token}` } });
    setAirbnbSettings(data);
    alert(language === 'pt' ? 'Preço Airbnb padrão guardado!' : 'Default Airbnb price saved!');
  };

  const handleCreateAirbnbBatch = async () => {
    const byDates = newAppointment.airbnb_mode === 'dates';
    if (!newAppointment.name || !newAppointment.phone || (!byDates && !newAppointment.preferred_date)) {
      alert(language === 'pt' ? 'Por favor preencha nome, telefone e data de início.' : 'Please fill name, phone and start date.');
      return;
    }
    if (byDates ? !newAppointment.airbnb_dates.length : !newAppointment.airbnb_weekdays.length) {
      alert(language === 'pt' ? (byDates ? 'Escolha pelo menos uma data.' : 'Escolha pelo menos um dia da semana.') : (byDates ? 'Select at least one date.' : 'Select at least one weekday.'));
      return;
    }
    setCreatingAppointment(true);
    try {
      const { data } = await axios.post(`${API}/admin/appointments/airbnb-batch`, {
        client_name: newAppointment.name,
        client_phone: newAppointment.phone,
        client_email: newAppointment.email || 'sem-email@sancur.ie',
        address: newAppointment.address,
        eircode: newAppointment.eircode,
        price: appointmentEstimate.price,
        time: newAppointment.preferred_time,
        weekdays: byDates ? [] : newAppointment.airbnb_weekdays,
        weeks: parseInt(newAppointment.airbnb_weeks) || 1,
        start_date: byDates ? (newAppointment.airbnb_dates[0] || null) : newAppointment.preferred_date,
        dates: byDates ? newAppointment.airbnb_dates : [],
        notes: newAppointment.notes,
        bedrooms: parseInt(newAppointment.bedrooms) || 0,
        bathrooms: parseInt(newAppointment.bathrooms) || 0,
        property_type: newAppointment.property_type
      }, { headers: { Authorization: `Bearer ${token}` } });
      closeAppointmentModal();
      await fetchAllData();
      setAirbnbGroupsKey(k => k + 1);
      setShowAirbnbGroups(true);
      alert(language === 'pt' ? `${data.created} limpezas Airbnb agendadas com sucesso!` : `${data.created} Airbnb cleanings scheduled successfully!`);
    } catch (error) {
      alert(language === 'pt' ? 'Erro ao criar agendamentos Airbnb.' : 'Error creating Airbnb appointments.');
    } finally {
      setCreatingAppointment(false);
    }
  };
  
  // Create manual appointment
  const handleCreateManualAppointment = async () => {
    if (!newAppointment.name || !newAppointment.phone || !newAppointment.preferred_date) {
      alert(language === 'pt' ? 'Por favor preencha nome, telefone e data.' : 'Please fill name, phone and date.');
      return;
    }
    
    setCreatingAppointment(true);
    try {
      const serviceNames = {
        regular: language === 'pt' ? 'Limpeza Regular' : 'Regular Cleaning',
        deep: language === 'pt' ? 'Limpeza Profunda' : 'Deep Cleaning',
        office: language === 'pt' ? 'Limpeza de Escritório' : 'Office Cleaning',
        endoftenancy: language === 'pt' ? 'Fim de Arrendamento' : 'End of Tenancy',
        upholstery: language === 'pt' ? 'Limpeza de Estofados' : 'Upholstery Cleaning'
      };
      
      // Determine service type name - use custom name if provided
      let finalServiceType;
      let finalPrice = appointmentEstimate.price;
      
      if (newAppointment.service_type === 'custom') {
        finalServiceType = newAppointment.custom_service_name || 'Serviço Personalizado';
        finalPrice = parseFloat(newAppointment.custom_service_price) || 0;
      } else {
        finalServiceType = serviceNames[newAppointment.service_type] || newAppointment.service_type;
      }
      
      if (newAppointment.service_type === 'custom') {
        finalPrice = appointmentEstimate.price;
      }
      const appointmentData = {
        original_price: appointmentEstimate.originalPrice,
        discount_type: newAppointment.discount_type !== 'none' ? newAppointment.discount_type : null,
        discount_value: newAppointment.discount_type !== 'none' ? parseFloat(newAppointment.discount_value) || 0 : null,
        discount_amount: appointmentEstimate.discount || 0,
        client_name: newAppointment.name,
        client_email: newAppointment.email || 'sem-email@sancur.ie',
        client_phone: newAppointment.phone,
        service_type: finalServiceType,
        address: newAppointment.address,
        eircode: newAppointment.eircode,
        preferred_date: newAppointment.preferred_date,
        preferred_time: newAppointment.preferred_time,
        notes: newAppointment.notes,
        status: 'scheduled',
        estimated_price: finalPrice,
        estimated_time: appointmentEstimate.time,
        property_type: newAppointment.property_type,
        bedrooms: parseInt(newAppointment.bedrooms) || 0,
        bathrooms: parseInt(newAppointment.bathrooms) || 0,
        living_rooms: parseInt(newAppointment.living_rooms) || 0,
        has_kitchen: newAppointment.has_kitchen,
        has_pets: newAppointment.has_pets,
        special_requests: newAppointment.special_requests,
        source: 'manual_whatsapp'
      };
      
      await axios.post(`${API}/admin/appointments`, appointmentData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reset form and close modal
      setNewAppointment({
        name: '', phone: '', email: '', address: '', eircode: '',
        service_type: 'regular', custom_service_name: '', custom_service_price: '',
        property_type: 'apartment',
        bedrooms: 2, bathrooms: 1, living_rooms: 1,
        has_kitchen: true, has_pets: false,
        preferred_date: '', preferred_time: '09:00',
        special_requests: '', notes: '',
        discount_type: 'none', discount_value: '', airbnb_price: '', airbnb_weekdays: [], airbnb_weeks: 4, airbnb_mode: 'weekdays', airbnb_dates: []
      });
      setShowNewAppointmentModal(false);
      
      // Refresh data
      await fetchAllData();
      alert(language === 'pt' ? 'Agendamento criado com sucesso!' : 'Appointment created successfully!');
    } catch (error) {
      
      alert(language === 'pt' ? 'Erro ao criar agendamento.' : 'Error creating appointment.');
    } finally {
      setCreatingAppointment(false);
    }
  };
  
  // Open edit appointment modal
  const openEditAppointment = (appointment) => {
    // Map service type back to id
    const serviceTypeMap = {
      'Limpeza Regular': 'regular',
      'Regular Cleaning': 'regular',
      'Regular House Cleaning': 'regular',
      'Limpeza Profunda': 'deep',
      'Deep Cleaning': 'deep',
      'Limpeza de Escritório': 'office',
      'Office Cleaning': 'office',
      'Fim de Arrendamento': 'endoftenancy',
      'End of Tenancy': 'endoftenancy',
      'End of Tenancy Cleaning': 'endoftenancy',
      'Limpeza de Estofados': 'upholstery',
      'Upholstery Cleaning': 'upholstery'
    };
    
    const isAirbnb = appointment.source === 'airbnb' || /airbnb/i.test(appointment.service_type || '');
    const knownId = serviceTypeMap[appointment.service_type];
    const serviceId = isAirbnb ? 'airbnb' : (knownId || 'custom');
    const basePrice = appointment.original_price ?? appointment.estimated_price ?? '';
    
    setEditingAppointment(appointment);
    setNewAppointment({
      custom_service_name: knownId || isAirbnb ? '' : (appointment.service_type || ''),
      custom_service_price: serviceId === 'custom' ? String(basePrice) : '',
      airbnb_price: isAirbnb ? String(basePrice) : '',
      airbnb_weekdays: [],
      airbnb_weeks: 4,
      airbnb_mode: 'weekdays',
      airbnb_dates: [],
      discount_type: appointment.discount_type || 'none',
      discount_value: appointment.discount_value ?? '',
      name: appointment.name || '',
      phone: appointment.phone || '',
      email: appointment.email || '',
      address: appointment.address || '',
      eircode: appointment.eircode || '',
      service_type: serviceId,
      property_type: appointment.property_type || 'apartment',
      bedrooms: appointment.bedrooms || 2,
      bathrooms: appointment.bathrooms || 1,
      living_rooms: appointment.living_rooms || 1,
      has_kitchen: appointment.has_kitchen !== false,
      has_pets: appointment.has_pets || false,
      preferred_date: appointment.preferred_date || appointment.date || '',
      preferred_time: appointment.preferred_time || appointment.time || '09:00',
      special_requests: appointment.special_requests || '',
      notes: appointment.notes || appointment.message || ''
    });
    setShowNewAppointmentModal(true);
  };
  
  // Update existing appointment
  const handleUpdateAppointment = async () => {
    if (!editingAppointment) return;
    
    setCreatingAppointment(true);
    try {
      const serviceNames = {
        regular: language === 'pt' ? 'Limpeza Regular' : 'Regular Cleaning',
        deep: language === 'pt' ? 'Limpeza Profunda' : 'Deep Cleaning',
        office: language === 'pt' ? 'Limpeza de Escritório' : 'Office Cleaning',
        endoftenancy: language === 'pt' ? 'Fim de Arrendamento' : 'End of Tenancy',
        upholstery: language === 'pt' ? 'Limpeza de Estofados' : 'Upholstery Cleaning'
      };
      
      const editedServiceType = newAppointment.service_type === 'custom'
        ? (newAppointment.custom_service_name || 'Serviço Personalizado')
        : newAppointment.service_type === 'airbnb'
          ? editingAppointment.service_type
          : (serviceNames[newAppointment.service_type] || newAppointment.service_type);
      const updateData = {
        original_price: appointmentEstimate.originalPrice,
        discount_type: newAppointment.discount_type !== 'none' ? newAppointment.discount_type : null,
        discount_value: newAppointment.discount_type !== 'none' ? parseFloat(newAppointment.discount_value) || 0 : null,
        discount_amount: appointmentEstimate.discount || 0,
        name: newAppointment.name,
        email: newAppointment.email || 'sem-email@sancur.ie',
        phone: newAppointment.phone,
        service_type: editedServiceType,
        address: newAppointment.address,
        eircode: newAppointment.eircode,
        date: newAppointment.preferred_date,
        time: newAppointment.preferred_time,
        preferred_date: newAppointment.preferred_date,
        preferred_time: newAppointment.preferred_time,
        notes: newAppointment.notes,
        estimated_price: appointmentEstimate.price,
        estimated_time: appointmentEstimate.time,
        property_type: newAppointment.property_type,
        bedrooms: parseInt(newAppointment.bedrooms) || 0,
        bathrooms: parseInt(newAppointment.bathrooms) || 0,
        living_rooms: parseInt(newAppointment.living_rooms) || 0,
        has_kitchen: newAppointment.has_kitchen,
        has_pets: newAppointment.has_pets,
        special_requests: newAppointment.special_requests
      };
      
      await axios.put(`${API}/admin/appointments/${editingAppointment.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reset form and close modal
      setEditingAppointment(null);
      setNewAppointment({
        name: '', phone: '', email: '', address: '', eircode: '',
        service_type: 'regular', custom_service_name: '', custom_service_price: '',
        property_type: 'apartment',
        bedrooms: 2, bathrooms: 1, living_rooms: 1,
        has_kitchen: true, has_pets: false,
        preferred_date: '', preferred_time: '09:00',
        special_requests: '', notes: '',
        discount_type: 'none', discount_value: '', airbnb_price: '', airbnb_weekdays: [], airbnb_weeks: 4, airbnb_mode: 'weekdays', airbnb_dates: []
      });
      setShowNewAppointmentModal(false);
      
      await fetchAllData();
      alert(language === 'pt' ? 'Agendamento atualizado com sucesso!' : 'Appointment updated successfully!');
    } catch (error) {
      
      alert(language === 'pt' ? 'Erro ao atualizar agendamento.' : 'Error updating appointment.');
    } finally {
      setCreatingAppointment(false);
    }
  };
  
  // Close modal and reset
  const closeAppointmentModal = () => {
    setShowNewAppointmentModal(false);
    setEditingAppointment(null);
    setNewAppointment({
      name: '', phone: '', email: '', address: '', eircode: '',
      service_type: 'regular', custom_service_name: '', custom_service_price: '',
      property_type: 'apartment',
      bedrooms: 2, bathrooms: 1, living_rooms: 1,
      has_kitchen: true, has_pets: false,
      preferred_date: '', preferred_time: '09:00',
      special_requests: '', notes: '',
      discount_type: 'none', discount_value: '', airbnb_price: '', airbnb_weekdays: [], airbnb_weeks: 4, airbnb_mode: 'weekdays', airbnb_dates: []
    });
  };

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [token]);

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/admin/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsRead = async () => {
    try {
      await axios.patch(`${API}/admin/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Manual refresh function
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData(true);
    setTimeout(() => setRefreshing(false), 500);
  };

  // Open image in modal
  const openImageModal = (url, title) => {
    setImageModal({ show: true, url, title });
  };

  // Close image modal
  const closeImageModal = () => {
    setImageModal({ show: false, url: '', title: '' });
  };

  // Set page title for admin dashboard
  useEffect(() => {
    document.title = language === 'pt' ? 'Sancur Admin | Painel de Controlo' : 'Sancur Admin | Control Panel';
  }, [language]);

  const fetchAllData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [contactsRes, appointmentsRes, clientsRes, staffRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/appointments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { clients: [] } })),
        axios.get(`${API}/admin/staff`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/work-sessions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { sessions: [] } }))
      ]);
      setContacts(contactsRes.data);
      setAppointments(appointmentsRes.data);
      setClients(clientsRes.data.clients || []);
      setStaff(staffRes.data.staff || []);
      setWorkSessions(sessionsRes.data.sessions || []);
    } catch (error) {
      
      if (error.response?.status === 401) {
        logout();
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout, navigate]);

  // Fetch expenses for finance tab
  const fetchExpenses = useCallback(async () => {
    try {
      const params = {};
      if (useCustomDates && customDateRange.start && customDateRange.end) {
        params.start_date = customDateRange.start;
        params.end_date = customDateRange.end;
      }
      const response = await axios.get(`${API}/admin/finance/expenses`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setExpenses(response.data || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  }, [token, useCustomDates, customDateRange]);

  useEffect(() => {
    if (financeUnlocked) {
      fetchExpenses();
    }
  }, [financeUnlocked, fetchExpenses]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    } else {
      fetchAllData();
      fetchNotifications();
      
      // Auto-refresh every 30 seconds
      const interval = setInterval(() => {
        fetchAllData(true); // silent refresh
        fetchNotifications(); // refresh notifications
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, navigate, fetchAllData, fetchNotifications]);

  const updateContactStatus = async (contactId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/contacts/${contactId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAllData();
    } catch (error) {
      
    }
  };

  // Accept quote and automatically create appointment
  const acceptQuoteAndCreateAppointment = async (contact) => {
    try {
      // 1. Update contact status to accepted
      await axios.patch(
        `${API}/admin/contacts/${contact.id}`,
        { status: 'accepted' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // 2. Create appointment from contact data
      const appointmentData = {
        client_name: contact.name,
        client_email: contact.email,
        client_phone: contact.phone,
        service_type: contact.service_type,
        address: contact.address || '',
        eircode: contact.eircode || '',
        preferred_date: contact.preferred_date,
        preferred_time: contact.preferred_time,
        notes: contact.message || '',
        status: 'scheduled',
        estimated_price: contact.estimated_price,
        photo_urls: contact.photo_urls || [],
        contact_id: contact.id, // Link to original contact
        // NEW: Include estimated time and property details
        estimated_time: contact.estimated_time,
        property_type: contact.property_type,
        bedrooms: contact.bedrooms,
        bathrooms: contact.bathrooms,
        living_rooms: contact.living_rooms,
        has_kitchen: contact.has_kitchen,
        has_pets: contact.has_pets,
        special_requests: contact.special_requests
      };

      await axios.post(
        `${API}/admin/appointments`,
        appointmentData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // 3. Refresh data and switch to appointments tab
      await fetchAllData();
      setActiveTab('appointments');
      
      alert(language === 'pt' 
        ? 'Orçamento aceite! Agendamento criado com sucesso.' 
        : 'Quote accepted! Appointment created successfully.');
    } catch (error) {
      
      alert(language === 'pt' 
        ? 'Erro ao aceitar orçamento. Tente novamente.' 
        : 'Error accepting quote. Please try again.');
    }
  };

  // Helper function to get total hours worked for a specific job
  const getJobWorkedHours = useCallback((jobId) => {
    const jobSessions = workSessions.filter(
      s => s.job_id === jobId && s.action === 'finish'
    );
    const totalSeconds = jobSessions.reduce((sum, s) => sum + (s.elapsed_seconds || 0), 0);
    return {
      hours: totalSeconds / 3600,
      sessions: jobSessions,
      hasManual: jobSessions.some(s => s.is_manual),
      hasTimer: jobSessions.some(s => !s.is_manual)
    };
  }, [workSessions]);

  // Get staff members assigned to a job
  const getJobStaffNames = useCallback((appointment) => {
    const staffIds = appointment.assigned_staff_ids || [];
    if (appointment.assigned_staff_id && !staffIds.includes(appointment.assigned_staff_id)) {
      staffIds.push(appointment.assigned_staff_id);
    }
    
    const staffNames = staffIds.map(id => {
      const staffMember = staff.find(s => s.id === id);
      return staffMember ? staffMember.name : null;
    }).filter(Boolean);
    
    // Also include from assigned_staff_names if available
    if (appointment.assigned_staff_names?.length) {
      return appointment.assigned_staff_names;
    }
    
    return staffNames;
  }, [staff]);

  // Finance calculations
  const calculateFinanceData = useCallback(() => {
    let periodStart, periodEnd;
    
    if (useCustomDates && customDateRange.start && customDateRange.end) {
      periodStart = new Date(customDateRange.start);
      periodEnd = new Date(customDateRange.end);
      periodEnd.setHours(23, 59, 59, 999);
    } else {
      periodEnd = new Date();
      periodStart = new Date();
      periodStart.setDate(periodStart.getDate() - financePeriod);
    }
    
    // Get completed appointments in the period
    const completedInPeriod = appointments.filter(apt => {
      if (apt.status !== 'completed') return false;
      const completedDate = new Date(apt.status_updated_at || apt.updated_at || apt.created_at);
      return completedDate >= periodStart && completedDate <= periodEnd;
    });
    
    // Calculate profit for each job
    const jobsWithProfit = completedInPeriod.map(apt => {
      // Use override price if set, otherwise use estimated_price
      const revenue = num(apt.estimated_price);
      
      // Check if there's a staff cost override
      if (apt.staff_cost_override !== undefined && apt.staff_cost_override !== null && apt.staff_cost_override !== '') {
        const staffCost = num(apt.staff_cost_override);
        const profit = revenue - staffCost;
        const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;
        
        return {
          ...apt,
          price: revenue,
          revenue,
          staffCost,
          profit,
          profitMargin,
          hoursWorked: 0,
          staffNames: getJobStaffNames(apt)
        };
      }
      
      // Get hours worked for this job from work sessions
      const jobSessions = workSessions.filter(s => s.job_id === apt.id && s.action === 'finish');
      const totalSeconds = jobSessions.reduce((sum, s) => sum + num(s.elapsed_seconds), 0);
      const hoursWorked = totalSeconds / 3600;
      
      // Get staff assigned and their rates
      const assignedStaffIds = [...(apt.assigned_staff_ids || [])];
      if (apt.assigned_staff_id && !assignedStaffIds.includes(apt.assigned_staff_id)) {
        assignedStaffIds.push(apt.assigned_staff_id);
      }
      
      // Calculate staff cost
      let staffCost = 0;
      assignedStaffIds.forEach(staffId => {
        const staffMember = staff.find(s => s.id === staffId);
        if (staffMember) {
          const rate = apt.service_type === 'Deep Cleaning' 
            ? num(staffMember.deep_rate || staffMember.hourly_rate, 15)
            : num(staffMember.regular_rate || staffMember.hourly_rate, 15);
          // Divide hours equally among staff if multiple
          const staffHours = hoursWorked / Math.max(assignedStaffIds.length, 1);
          staffCost += staffHours * rate;
        }
      });
      
      // If no hours recorded, estimate from service type
      if (hoursWorked === 0 && revenue > 0) {
        // Estimate hours based on price (rough estimate)
        const estimatedHours = num(apt.estimated_time, 0) || (revenue / 30);
        assignedStaffIds.forEach(staffId => {
          const staffMember = staff.find(s => s.id === staffId);
          if (staffMember) {
            const rate = num(staffMember.regular_rate || staffMember.hourly_rate, 15);
            staffCost += (estimatedHours / Math.max(assignedStaffIds.length, 1)) * rate;
          }
        });
      }
      
      const profit = revenue - staffCost;
      const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;
      
      return {
        ...apt,
        price: revenue,
        revenue,
        staffCost,
        profit,
        profitMargin,
        hoursWorked,
        staffNames: getJobStaffNames(apt)
      };
    });
    
    // Filter expenses for the period
    const expensesInPeriod = expenses.filter(exp => {
      const expDate = new Date(exp.date);
      return expDate >= periodStart && expDate <= periodEnd;
    });
    
    const totalExpenses = expensesInPeriod.reduce((sum, e) => sum + num(e.amount), 0);
    const fixedExpenses = expensesInPeriod.filter(e => e.expense_type === 'fixed').reduce((sum, e) => sum + num(e.amount), 0);
    const serviceExpenses = expensesInPeriod.filter(e => e.expense_type === 'service').reduce((sum, e) => sum + num(e.amount), 0);
    
    // Calculate totals
    const totalRevenue = jobsWithProfit.reduce((sum, j) => sum + j.revenue, 0);
    const totalStaffCost = jobsWithProfit.reduce((sum, j) => sum + j.staffCost, 0);
    const totalProfit = totalRevenue - totalStaffCost - totalExpenses;
    const avgProfitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
    
    return {
      jobs: jobsWithProfit.sort((a, b) => new Date(b.status_updated_at || b.updated_at) - new Date(a.status_updated_at || a.updated_at)),
      expenses: expensesInPeriod,
      totalRevenue,
      totalStaffCost,
      totalExpenses,
      fixedExpenses,
      serviceExpenses,
      totalProfit,
      avgProfitMargin,
      totalJobs: jobsWithProfit.length,
      periodStart,
      periodEnd
    };
  }, [appointments, workSessions, staff, expenses, financePeriod, useCustomDates, customDateRange, getJobStaffNames]);

  // Handle finance tab access - verify PIN with server
  const handleFinanceAccess = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/finance/verify-pin`,
        { pin: financePassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.valid) {
        setFinanceUnlocked(true);
        setFinancePasswordError('');
        setFinancePassword('');
      } else {
        setFinancePasswordError(language === 'pt' ? 'PIN incorreto' : 'Incorrect PIN');
      }
    } catch (error) {
      setFinancePasswordError(language === 'pt' ? 'PIN incorreto' : 'Incorrect PIN');
    }
  };

  // Handle PIN change - only for super admin
  const handleChangePinSubmit = async () => {
    setPinChangeError('');
    setPinChangeSuccess('');
    
    // Validate inputs
    if (!currentPinInput || !newPinInput || !confirmPinInput) {
      setPinChangeError(language === 'pt' ? 'Preencha todos os campos' : 'Fill all fields');
      return;
    }
    
    if (newPinInput !== confirmPinInput) {
      setPinChangeError(language === 'pt' ? 'Os novos PINs não coincidem' : 'New PINs do not match');
      return;
    }
    
    if (!/^\d{4,6}$/.test(newPinInput)) {
      setPinChangeError(language === 'pt' ? 'O PIN deve ter 4-6 dígitos' : 'PIN must be 4-6 digits');
      return;
    }
    
    setChangingPin(true);
    
    try {
      await axios.put(
        `${API}/admin/finance/update-pin`,
        { current_pin: currentPinInput, new_pin: newPinInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPinChangeSuccess(language === 'pt' ? 'PIN alterado com sucesso!' : 'PIN changed successfully!');
      setCurrentPinInput('');
      setNewPinInput('');
      setConfirmPinInput('');
      
      // Auto close after 2 seconds
      setTimeout(() => {
        setShowChangePinModal(false);
        setPinChangeSuccess('');
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (language === 'pt' ? 'Erro ao alterar PIN' : 'Error changing PIN');
      setPinChangeError(errorMsg);
    } finally {
      setChangingPin(false);
    }
  };

  // Expense management functions
  const openExpenseModal = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setExpenseForm({
        name: expense.name,
        amount: expense.amount.toString(),
        expense_type: expense.expense_type,
        category: expense.category,
        date: expense.date,
        notes: expense.notes || '',
        recurring: expense.recurring || false
      });
    } else {
      setEditingExpense(null);
      setExpenseForm({
        name: '',
        amount: '',
        expense_type: 'fixed',
        category: 'other',
        date: new Date().toISOString().split('T')[0],
        notes: '',
        recurring: false
      });
    }
    setShowExpenseModal(true);
  };

  const closeExpenseModal = () => {
    setShowExpenseModal(false);
    setEditingExpense(null);
    setExpenseForm({
      name: '',
      amount: '',
      expense_type: 'fixed',
      category: 'other',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      recurring: false
    });
  };

  const handleSaveExpense = async () => {
    if (!expenseForm.name || !expenseForm.amount) return;
    
    setSavingExpense(true);
    try {
      const payload = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      };
      
      if (editingExpense) {
        await axios.put(
          `${API}/admin/finance/expenses/${editingExpense.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/admin/finance/expenses`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      fetchExpenses();
      closeExpenseModal();
    } catch (error) {
      console.error('Error saving expense:', error);
    } finally {
      setSavingExpense(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm(language === 'pt' ? 'Tem certeza que deseja eliminar este custo?' : 'Are you sure you want to delete this expense?')) {
      return;
    }
    
    try {
      await axios.delete(
        `${API}/admin/finance/expenses/${expenseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  // Edit service financial values
  const openEditServiceModal = (job) => {
    setEditingService(job);
    setServiceFinanceForm({
      estimated_price: job.price?.toString() || '',
      staff_cost: job.staffCost?.toString() || '',
      notes: job.finance_notes || ''
    });
    setShowEditServiceModal(true);
  };

  const closeEditServiceModal = () => {
    setShowEditServiceModal(false);
    setEditingService(null);
    setServiceFinanceForm({ estimated_price: '', staff_cost: '', notes: '' });
  };

  const handleSaveServiceFinance = async () => {
    if (!editingService) return;
    
    setSavingServiceFinance(true);
    try {
      const payload = {};
      if (serviceFinanceForm.estimated_price) {
        payload.estimated_price = parseFloat(serviceFinanceForm.estimated_price);
      }
      if (serviceFinanceForm.staff_cost) {
        payload.staff_cost = parseFloat(serviceFinanceForm.staff_cost);
      }
      if (serviceFinanceForm.notes) {
        payload.notes = serviceFinanceForm.notes;
      }
      
      await axios.put(
        `${API}/admin/appointments/${editingService.id}/finance`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchAllData();
      closeEditServiceModal();
    } catch (error) {
      console.error('Error saving service finance:', error);
    } finally {
      setSavingServiceFinance(false);
    }
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/appointments/${appointmentId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAllData();
    } catch (error) {
      
    }
  };

  // Delete functions
  const openDeleteModal = (type, id, name) => {
    setDeleteModal({ show: true, type, id, name });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, type: '', id: '', name: '' });
  };

  const confirmDelete = async () => {
    try {
      if (deleteModal.type === 'contact') {
        await axios.delete(
          `${API}/admin/contacts/${deleteModal.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else if (deleteModal.type === 'appointment') {
        await axios.delete(
          `${API}/admin/appointments/${deleteModal.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      closeDeleteModal();
      fetchAllData();
    } catch (error) {
      
      alert(language === 'pt' ? 'Erro ao excluir. Tente novamente.' : 'Error deleting. Please try again.');
    }
  };

  // Filtered contacts based on search and filters
  // NOTE: Accepted quotes should NOT appear here - they're now in Appointments
  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      // Hide accepted quotes - they've been converted to appointments
      if (contact.status === 'accepted') return false;
      
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        contact.name.toLowerCase().includes(searchLower) ||
        contact.email.toLowerCase().includes(searchLower) ||
        contact.phone.includes(searchQuery);
      
      // Date filter
      let matchesDate = true;
      if (dateFilter.startDate || dateFilter.endDate) {
        const contactDate = new Date(contact.timestamp);
        if (dateFilter.startDate) {
          matchesDate = matchesDate && contactDate >= new Date(dateFilter.startDate);
        }
        if (dateFilter.endDate) {
          const endDate = new Date(dateFilter.endDate);
          endDate.setHours(23, 59, 59, 999);
          matchesDate = matchesDate && contactDate <= endDate;
        }
      }
      
      // Status filter - only show pending or rejected, not accepted
      const matchesStatus = statusFilter === 'all' || contact.status === statusFilter;
      
      // Region filter
      const matchesRegion = regionFilter === 'all' || contact.region === regionFilter;
      
      return matchesSearch && matchesDate && matchesStatus && matchesRegion;
    });
  }, [contacts, searchQuery, dateFilter, statusFilter, regionFilter]);

  // Filtered appointments
  const filteredAppointments = useMemo(() => {
    return appointments.filter(appt => {
      // Hide completed appointments - they're now in "Concluídos" tab
      if (appt.status === 'completed') return false;
      
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        appt.name.toLowerCase().includes(searchLower) ||
        appt.email.toLowerCase().includes(searchLower) ||
        appt.phone.includes(searchQuery);
      
      let matchesDate = true;
      if (dateFilter.startDate || dateFilter.endDate) {
        const apptDate = new Date(appt.created_at);
        if (dateFilter.startDate) {
          matchesDate = matchesDate && apptDate >= new Date(dateFilter.startDate);
        }
        if (dateFilter.endDate) {
          const endDate = new Date(dateFilter.endDate);
          endDate.setHours(23, 59, 59, 999);
          matchesDate = matchesDate && apptDate <= endDate;
        }
      }
      
      const matchesStatus = statusFilter === 'all' || appt.status === statusFilter;
      
      // Region filter
      const matchesRegion = regionFilter === 'all' || appt.region === regionFilter;
      
      return matchesSearch && matchesDate && matchesStatus && matchesRegion;
    });
  }, [appointments, searchQuery, dateFilter, statusFilter, regionFilter]);

  const clearFilters = () => {
    setSearchQuery('');
    setDateFilter({ startDate: '', endDate: '' });
    setStatusFilter('all');
    setRegionFilter('all');
  };

  const hasActiveFilters = searchQuery || dateFilter.startDate || dateFilter.endDate || statusFilter !== 'all' || regionFilter !== 'all';

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(language === 'pt' ? 'pt-PT' : 'en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  const getRoleBadge = () => {
    const roleConfig = {
      super_admin: { label: language === 'pt' ? 'Super Admin' : 'Super Admin', color: 'bg-red-100 text-red-700' },
      manager: { label: language === 'pt' ? 'Gestor' : 'Manager', color: 'bg-blue-100 text-blue-700' },
      viewer: { label: language === 'pt' ? 'Visualizador' : 'Viewer', color: 'bg-gray-100 text-gray-700' }
    };
    const config = roleConfig[user?.role] || roleConfig.viewer;
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-4">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-4 min-w-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/l9fyih4f_Whisk_4dd066d44f7c3e88b4a47b0cc96820dddr-removebg-preview.png" 
                alt="Sancur Services" 
                className="h-10 sm:h-16 w-auto"
              />
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-gray-900">{user?.name || 'Admin'}</p>
                <div className="flex items-center gap-2 justify-end">
                  <span className="text-xs text-gray-500">{user?.email}</span>
                  {getRoleBadge()}
                </div>
              </div>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="admin-refresh-button"
                className="flex items-center px-2.5 sm:px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors font-body disabled:opacity-50"
                title={language === 'pt' ? 'Atualizar dados' : 'Refresh data'}
              >
                <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
              </button>
              
              {/* Notifications Bell */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                  data-testid="notifications-button"
                >
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="fixed sm:absolute left-3 right-3 sm:left-auto sm:right-0 top-16 sm:top-auto mt-2 sm:w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
                    <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">
                        {language === 'pt' ? 'Notificações' : 'Notifications'}
                      </h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllNotificationsRead}
                          className="text-xs text-primary hover:underline"
                        >
                          {language === 'pt' ? 'Marcar todas como lidas' : 'Mark all read'}
                        </button>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="p-4 text-center text-gray-500 text-sm">
                          {language === 'pt' ? 'Sem notificações' : 'No notifications'}
                        </p>
                      ) : (
                        notifications.map((notif) => (
                          <div
                            key={notif.id}
                            onClick={() => markNotificationRead(notif.id)}
                            className={`p-3 border-b hover:bg-gray-50 cursor-pointer ${!notif.read ? 'bg-blue-50' : ''}`}
                          >
                            <p className="text-sm text-gray-900">{notif.message}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(notif.timestamp).toLocaleString('pt-PT', { 
                                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
                              })}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={handleLogout}
                data-testid="admin-logout-button"
                className="flex items-center px-2.5 sm:px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors font-body"
                title={language === 'pt' ? 'Sair' : 'Logout'}
              >
                <LogOut size={20} className="sm:mr-2" />
                <span className="hidden sm:inline">{language === 'pt' ? 'Sair' : 'Logout'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {reminderCount > 0 && activeTab !== 'whatsapp' && (
          <button onClick={() => setActiveTab('whatsapp')} data-testid="reminders-banner"
            className="w-full mb-6 flex items-center justify-between gap-3 bg-[#25D366]/10 border border-[#25D366]/40 text-left rounded-xl px-4 py-3 hover:bg-[#25D366]/20 transition-colors">
            <span className="flex items-center gap-2 text-gray-800 font-medium">
              <Bell size={18} className="text-[#25D366]" />
              {language === 'pt'
                ? `${reminderCount} lembrete${reminderCount > 1 ? 's' : ''} de avaliação pronto${reminderCount > 1 ? 's' : ''} a enviar por WhatsApp`
                : `${reminderCount} review reminder${reminderCount > 1 ? 's' : ''} ready to send via WhatsApp`}
            </span>
            <span className="text-sm text-[#1a5c3a] font-semibold whitespace-nowrap">{language === 'pt' ? 'Ver →' : 'View →'}</span>
          </button>
        )}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="flex border-b overflow-x-auto">
            <button
              onClick={() => setActiveTab('stats')}
              className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-body font-medium transition-colors whitespace-nowrap ${
                activeTab === 'stats'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-600 hover:text-primary'
              }`}
              data-testid="tab-stats"
            >
              <BarChart3 size={20} className="mr-2" />
              {language === 'pt' ? 'Estatísticas' : 'Statistics'}
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-body font-medium transition-colors whitespace-nowrap ${
                activeTab === 'contacts'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-600 hover:text-primary'
              }`}
              data-testid="tab-contacts"
            >
              <Mail size={20} className="mr-2" />
              {language === 'pt' ? 'Pedidos de Orçamento' : 'Quote Requests'}
              <span className="ml-2 px-2 py-1 bg-amber-500 text-white rounded-full text-xs">
                {contacts.filter(c => c.status === 'pending').length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('appointments')}
              className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-body font-medium transition-colors whitespace-nowrap ${
                activeTab === 'appointments'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-600 hover:text-primary'
              }`}
              data-testid="tab-appointments"
            >
              <Calendar size={20} className="mr-2" />
              {language === 'pt' ? 'Agendamentos' : 'Appointments'}
              <span className="ml-2 px-2 py-1 bg-primary text-white rounded-full text-xs">
                {appointments.filter(a => a.status !== 'completed').length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-body font-medium transition-colors whitespace-nowrap ${
                activeTab === 'completed'
                  ? 'border-b-2 border-emerald-500 text-emerald-600'
                  : 'text-gray-600 hover:text-emerald-500'
              }`}
              data-testid="tab-completed"
            >
              <CheckCircle size={20} className="mr-2" />
              {language === 'pt' ? 'Concluídos' : 'Completed'}
              <span className="ml-2 px-2 py-1 bg-emerald-500 text-white rounded-full text-xs">
                {appointments.filter(a => a.status === 'completed').length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-body font-medium transition-colors whitespace-nowrap ${
                activeTab === 'gallery'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-600 hover:text-primary'
              }`}
              data-testid="tab-gallery"
            >
              <Image size={20} className="mr-2" />
              {language === 'pt' ? 'Galeria' : 'Gallery'}
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-body font-medium transition-colors whitespace-nowrap ${
                activeTab === 'calendar'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-600 hover:text-primary'
              }`}
              data-testid="tab-calendar"
            >
              <Calendar size={20} className="mr-2" />
              {language === 'pt' ? 'Calendário' : 'Calendar'}
            </button>
            <button
              onClick={() => setActiveTab('clients')}
              className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-body font-medium transition-colors whitespace-nowrap ${
                activeTab === 'clients'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-600 hover:text-primary'
              }`}
              data-testid="tab-clients"
            >
              <History size={20} className="mr-2" />
              {language === 'pt' ? 'Histórico Clientes' : 'Client History'}
            </button>
            <button
              onClick={() => setActiveTab('staff')}
              className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-body font-medium transition-colors whitespace-nowrap ${
                activeTab === 'staff'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-600 hover:text-primary'
              }`}
              data-testid="tab-staff"
            >
              <UserCog size={20} className="mr-2" />
              {language === 'pt' ? 'Funcionários' : 'Staff'}
            </button>
            <button
              onClick={() => setActiveTab('hours')}
              className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-body font-medium transition-colors whitespace-nowrap ${
                activeTab === 'hours'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-600 hover:text-primary'
              }`}
              data-testid="tab-hours"
            >
              <Clock size={20} className="mr-2" />
              {language === 'pt' ? 'Horas' : 'Hours'}
            </button>
            <button
              onClick={() => setActiveTab('finance')}
              className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-body font-medium transition-colors whitespace-nowrap ${
                activeTab === 'finance'
                  ? 'border-b-2 border-amber-500 text-amber-600'
                  : 'text-gray-600 hover:text-amber-500'
              }`}
              data-testid="tab-finance"
            >
              <TrendingUp size={20} className="mr-2" />
              {language === 'pt' ? 'Finanças' : 'Finance'}
              {financeUnlocked && (
                <span className="ml-1 w-2 h-2 bg-green-500 rounded-full"></span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-body font-medium transition-colors whitespace-nowrap ${
                activeTab === 'users'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-600 hover:text-primary'
              }`}
              data-testid="tab-users"
            >
              <Shield size={20} className="mr-2" />
              {language === 'pt' ? 'Utilizadores' : 'Users'}
            </button>
            <button
              onClick={() => setActiveTab('whatsapp')}
              className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-body font-medium transition-colors whitespace-nowrap ${
                activeTab === 'whatsapp'
                  ? 'border-b-2 border-[#25D366] text-[#1a5c3a]'
                  : 'text-gray-600 hover:text-[#1a5c3a]'
              }`}
              data-testid="tab-whatsapp"
            >
              <MessageSquare size={20} className="mr-2" />
              WhatsApp
              {reminderCount > 0 && (
                <span className="ml-2 text-xs bg-[#25D366] text-white rounded-full px-2 py-0.5" data-testid="tab-whatsapp-badge">{reminderCount}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-body font-medium transition-colors whitespace-nowrap ${
                activeTab === 'reviews' ? 'border-b-2 border-[#FBBC05] text-[#1a5c3a]' : 'text-gray-600 hover:text-[#1a5c3a]'
              }`}
              data-testid="tab-reviews"
            >
              <Star size={20} className="mr-2" />
              {language === 'pt' ? 'Avaliações' : 'Reviews'}
            </button>
          </div>

          {/* Filters - Show on contacts and appointments tabs */}
          {(activeTab === 'contacts' || activeTab === 'appointments') && (
            <div className="p-4 bg-gray-50 border-b">
              <div className="flex flex-wrap gap-4 items-end">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {language === 'pt' ? 'Pesquisar' : 'Search'}
                  </label>
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={language === 'pt' ? 'Nome, email ou telefone...' : 'Name, email or phone...'}
                      className="w-full h-10 pl-9 pr-4 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                      data-testid="search-input"
                    />
                  </div>
                </div>
                
                {/* Date From */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {language === 'pt' ? 'De' : 'From'}
                  </label>
                  <input
                    type="date"
                    value={dateFilter.startDate}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                    className="h-10 px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                    data-testid="date-from"
                  />
                </div>
                
                {/* Date To */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {language === 'pt' ? 'Até' : 'To'}
                  </label>
                  <input
                    type="date"
                    value={dateFilter.endDate}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                    className="h-10 px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                    data-testid="date-to"
                  />
                </div>
                
                {/* Region Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {language === 'pt' ? 'Região' : 'Region'}
                  </label>
                  <select
                    value={regionFilter}
                    onChange={(e) => setRegionFilter(e.target.value)}
                    className="h-10 px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                    data-testid="region-filter"
                  >
                    <option value="all">{language === 'pt' ? 'Todas' : 'All'}</option>
                    <option value="pt">🇵🇹 Portugal</option>
                    <option value="ie">🇮🇪 Ireland</option>
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {language === 'pt' ? 'Estado' : 'Status'}
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-10 px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                    data-testid="status-filter"
                  >
                    <option value="all">{language === 'pt' ? 'Todos' : 'All'}</option>
                    <option value="pending">{language === 'pt' ? 'Pendente' : 'Pending'}</option>
                    <option value="accepted">{language === 'pt' ? '✅ Aceite (ver perdidos)' : '✅ Accepted (view lost)'}</option>
                    <option value="in_progress">{language === 'pt' ? 'Em Andamento' : 'In Progress'}</option>
                    <option value="completed">{language === 'pt' ? 'Concluído' : 'Completed'}</option>
                    <option value="scheduled">{language === 'pt' ? 'Agendado' : 'Scheduled'}</option>
                  </select>
                </div>
                
                {/* Clear Filters */}
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="h-10 px-4 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors flex items-center gap-1"
                    data-testid="clear-filters"
                  >
                    <X size={14} />
                    {language === 'pt' ? 'Limpar' : 'Clear'}
                  </button>
                )}
              </div>
              
              {/* Results count */}
              {hasActiveFilters && (
                <p className="text-xs text-gray-500 mt-2">
                  {`${filteredAppointments.length} ${language === 'pt' ? 'resultado(s) encontrado(s)' : 'result(s) found'}`}
                </p>
              )}
            </div>
          )}

          <div className="p-6">
            {/* Statistics Tab */}
            {activeTab === 'stats' && (
              <AdminStats 
                contacts={contacts} 
                appointments={appointments} 
                language={language}
                dateFilter={dateFilter}
              />
            )}

            {activeTab === 'contacts' && (
              <div>
                <h2 className="text-xl font-heading font-semibold mb-4">
                  {language === 'pt' ? 'Pedidos de Orçamento' : 'Quote Requests'}
                </h2>
                {loading ? (
                  <p>{language === 'pt' ? 'Carregando...' : 'Loading...'}</p>
                ) : filteredContacts.length === 0 ? (
                  <p className="text-gray-600">{language === 'pt' ? 'Nenhum pedido encontrado' : 'No requests found'}</p>
                ) : (
                  <div className="space-y-4">
                    {filteredContacts.map((contact, index) => (
                      <div key={contact.id} className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid={`contact-${index}`}>
                        {/* Header */}
                        <div className="flex justify-between items-start mb-4 pb-3 border-b border-gray-100">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              contact.gender === 'male' ? 'bg-blue-100' : 
                              contact.gender === 'female' ? 'bg-pink-100' : 'bg-primary/10'
                            }`}>
                              <Users size={24} className={
                                contact.gender === 'male' ? 'text-blue-600' : 
                                contact.gender === 'female' ? 'text-pink-600' : 'text-primary'
                              } />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-heading font-semibold text-lg text-gray-900">{contact.name}</h3>
                                {contact.gender && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    contact.gender === 'male' ? 'bg-blue-100 text-blue-700' : 
                                    contact.gender === 'female' ? 'bg-pink-100 text-pink-700' : 'bg-purple-100 text-purple-700'
                                  }`}>
                                    {contact.gender === 'male' ? (language === 'pt' ? 'M' : 'M') : 
                                     contact.gender === 'female' ? (language === 'pt' ? 'F' : 'F') : 
                                     (language === 'pt' ? 'Outro' : 'Other')}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <Clock size={12} />
                                {formatDate(contact.timestamp)}
                              </p>
                            </div>
                          </div>
                          <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(contact.status)}`}>
                            {contact.status === 'pending' && <AlertCircle size={12} />}
                            {contact.status === 'accepted' && <CheckCircle size={12} />}
                            {contact.status === 'in_progress' && <Clock size={12} />}
                            {contact.status === 'completed' && <CheckCircle size={12} />}
                            {contact.status === 'pending' && (language === 'pt' ? 'Pendente' : 'Pending')}
                            {contact.status === 'accepted' && (language === 'pt' ? 'Aceite' : 'Accepted')}
                            {contact.status === 'in_progress' && (language === 'pt' ? 'Em Andamento' : 'In Progress')}
                            {contact.status === 'completed' && (language === 'pt' ? 'Concluído' : 'Completed')}
                          </span>
                        </div>
                        
                        {/* Contact Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                          <div className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-lg">
                            <Phone size={16} className="text-primary" />
                            <a href={`tel:${contact.phone}`} className="text-gray-700 hover:text-primary">{contact.phone}</a>
                          </div>
                          <div className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-lg">
                            <Mail size={16} className="text-primary" />
                            <a href={`mailto:${contact.email}`} className="text-gray-700 hover:text-primary truncate">{contact.email}</a>
                          </div>
                          <div className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-lg">
                            <Briefcase size={16} className="text-primary" />
                            <span className="text-gray-700">{contact.service_type}</span>
                          </div>
                          {/* Preferred Date and Time - Important! */}
                          {contact.preferred_date && (
                            <div className="flex items-center gap-2 text-sm bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                              <Calendar size={16} className="text-blue-600" />
                              <span className="text-blue-700 font-semibold">
                                {new Date(contact.preferred_date).toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-IE', { weekday: 'short', day: 'numeric', month: 'short' })}
                              </span>
                            </div>
                          )}
                          {contact.preferred_time && (
                            <div className="flex items-center gap-2 text-sm bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                              <Clock size={16} className="text-blue-600" />
                              <span className="text-blue-700 font-semibold">{contact.preferred_time}</span>
                            </div>
                          )}
                          {contact.estimated_price && (
                            <div className="flex items-center gap-2 text-sm bg-green-50 px-3 py-2 rounded-lg border border-green-200">
                              <DollarSign size={16} className="text-green-600" />
                              <span className="text-green-700 font-semibold">€{contact.estimated_price.toFixed(2)}</span>
                            </div>
                          )}
                          {/* Estimated Time */}
                          {contact.estimated_time && (
                            <div className="flex items-center gap-2 text-sm bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                              <Clock size={16} className="text-purple-600" />
                              <span className="text-purple-700 font-medium">{contact.estimated_time}</span>
                            </div>
                          )}
                          {contact.address && (
                            <div className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-lg md:col-span-2 lg:col-span-3">
                              <MapPin size={16} className="text-primary flex-shrink-0" />
                              <span className="text-gray-700">
                                {contact.address}
                                {contact.eircode && <span className="ml-2 font-medium text-primary">({contact.eircode})</span>}
                              </span>
                            </div>
                          )}
                          {!contact.address && contact.eircode && (
                            <div className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-lg">
                              <MapPin size={16} className="text-primary flex-shrink-0" />
                              <span className="text-gray-700">Eircode: <span className="font-medium text-primary">{contact.eircode}</span></span>
                            </div>
                          )}
                        </div>
                        
                        {/* Property Details */}
                        {(contact.property_type || contact.bedrooms || contact.bathrooms || contact.living_rooms) && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                            {contact.property_type && (
                              <div className="text-center">
                                <p className="text-xs text-gray-500">{language === 'pt' ? 'Tipo' : 'Type'}</p>
                                <p className="text-sm font-medium text-gray-700 capitalize">{contact.property_type}</p>
                              </div>
                            )}
                            {contact.bedrooms !== undefined && (
                              <div className="text-center">
                                <p className="text-xs text-gray-500">{language === 'pt' ? 'Quartos' : 'Bedrooms'}</p>
                                <p className="text-sm font-medium text-gray-700">{contact.bedrooms}</p>
                              </div>
                            )}
                            {contact.bathrooms !== undefined && (
                              <div className="text-center">
                                <p className="text-xs text-gray-500">{language === 'pt' ? 'Casas de Banho' : 'Bathrooms'}</p>
                                <p className="text-sm font-medium text-gray-700">{contact.bathrooms}</p>
                              </div>
                            )}
                            {contact.living_rooms !== undefined && (
                              <div className="text-center">
                                <p className="text-xs text-gray-500">{language === 'pt' ? 'Salas' : 'Living Rooms'}</p>
                                <p className="text-sm font-medium text-gray-700">{contact.living_rooms}</p>
                              </div>
                            )}
                            {contact.has_kitchen && (
                              <div className="text-center">
                                <p className="text-xs text-gray-500">{language === 'pt' ? 'Cozinha' : 'Kitchen'}</p>
                                <p className="text-sm font-medium text-green-600">✓</p>
                              </div>
                            )}
                            {contact.has_pets && (
                              <div className="text-center">
                                <p className="text-xs text-gray-500">{language === 'pt' ? 'Animais' : 'Pets'}</p>
                                <p className="text-sm font-medium text-amber-600">🐾</p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Special Requests */}
                        {contact.special_requests && (
                          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded-r-lg mb-4">
                            <p className="text-xs font-medium text-amber-700 mb-1">
                              {language === 'pt' ? 'Pedidos Especiais' : 'Special Requests'}
                            </p>
                            <p className="text-sm text-gray-700">{contact.special_requests}</p>
                          </div>
                        )}
                        
                        {/* Message */}
                        <div className="bg-blue-50 border-l-4 border-primary p-3 rounded-r-lg mb-4">
                          <p className="text-xs font-medium text-primary mb-1 flex items-center gap-1">
                            <MessageSquare size={12} />
                            {language === 'pt' ? 'Mensagem do Cliente' : 'Client Message'}
                          </p>
                          <p className="text-sm text-gray-700">{contact.message}</p>
                        </div>
                        
                        {/* Photos */}
                        {contact.photo_urls && contact.photo_urls.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                              <Image size={12} />
                              {language === 'pt' ? 'Fotos Enviadas pelo Cliente' : 'Photos Sent by Client'} ({contact.photo_urls.length})
                            </p>
                            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                              {contact.photo_urls.map((photo, idx) => (
                                <div 
                                  key={idx} 
                                  className="relative group cursor-pointer"
                                  onClick={() => openImageModal(photo, `${contact.name} - ${language === 'pt' ? 'Foto' : 'Photo'} ${idx + 1}`)}
                                  data-testid={`contact-photo-${idx}`}
                                >
                                  <img
                                    src={photo}
                                    alt={`${language === 'pt' ? 'Foto' : 'Photo'} ${idx + 1}`}
                                    className="w-full h-16 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity"
                                    onError={(e) => e.target.src = 'https://via.placeholder.com/100x80?text=Erro'}
                                  />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center pointer-events-none">
                                    <ExternalLink size={14} className="text-white" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Status Update */}
                        <div className="flex flex-col gap-2 pt-3 border-t border-gray-100">
                          {/* Show who updated - Only for Super Admin */}
                          {isSuperAdmin() && contact.status_updated_by && (
                            <div className="flex items-center gap-2 text-xs bg-yellow-50 border border-yellow-200 px-3 py-2 rounded-lg">
                              <UserCheck size={14} className="text-yellow-600" />
                              <span className="text-yellow-800">
                                {language === 'pt' ? 'Atualizado por' : 'Updated by'}: <strong>{contact.status_updated_by_name || contact.status_updated_by}</strong>
                                {contact.status_updated_at && (
                                  <span className="text-yellow-600 ml-2">
                                    ({new Date(contact.status_updated_at).toLocaleString('pt-PT')})
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            {/* Action buttons based on status */}
                            <div className="flex items-center gap-2">
                              {contact.status === 'pending' && (
                                <button
                                  onClick={() => acceptQuoteAndCreateAppointment(contact)}
                                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                                  data-testid={`accept-contact-${index}`}
                                >
                                  <CheckCircle size={16} />
                                  {language === 'pt' ? 'Aceitar e Agendar' : 'Accept & Schedule'}
                                </button>
                              )}
                              {contact.status === 'pending' && (
                                <button
                                  onClick={() => updateContactStatus(contact.id, 'rejected')}
                                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                                  data-testid={`reject-contact-${index}`}
                                >
                                  {language === 'pt' ? 'Recusar' : 'Reject'}
                                </button>
                              )}
                              {contact.status === 'accepted' && (
                                <>
                                  <span className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium flex items-center gap-2">
                                    <CheckCircle size={16} />
                                    {language === 'pt' ? 'Aceite' : 'Accepted'}
                                  </span>
                                  <button
                                    onClick={() => acceptQuoteAndCreateAppointment(contact)}
                                    className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors flex items-center gap-2"
                                    data-testid={`recreate-appointment-${index}`}
                                  >
                                    <RefreshCw size={16} />
                                    {language === 'pt' ? 'Recriar Agendamento' : 'Recreate Appointment'}
                                  </button>
                                </>
                              )}
                            </div>
                            <button
                              onClick={() => openDeleteModal('contact', contact.id, contact.name)}
                              className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors flex items-center gap-1"
                              data-testid={`delete-contact-${index}`}
                            >
                              <Trash2 size={14} />
                              {language === 'pt' ? 'Excluir' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'appointments' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <h2 className="text-xl font-heading font-semibold">
                    {language === 'pt' ? 'Agendamentos' : 'Appointments'}
                  </h2>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => setShowAirbnbGroups(v => !v)}
                      className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm rounded-lg border transition-colors whitespace-nowrap ${showAirbnbGroups ? 'bg-rose-500 border-rose-500 text-white' : 'border-rose-300 text-rose-700 hover:bg-rose-50'}`}
                      data-testid="toggle-airbnb-groups"
                    >
                      🏡 {language === 'pt' ? 'Séries Airbnb' : 'Airbnb Series'}
                    </button>
                    <button
                      onClick={() => setShowNewAppointmentModal(true)}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                      data-testid="new-appointment-btn"
                    >
                      <Plus size={18} />
                      {language === 'pt' ? 'Novo' : 'New'}<span className="hidden sm:inline">&nbsp;{language === 'pt' ? 'Agendamento' : 'Appointment'}</span>
                    </button>
                  </div>
                </div>
                {showAirbnbGroups && (
                  <div className="mb-6 bg-rose-50/40 border border-rose-100 rounded-xl p-4">
                    <AdminAirbnbGroups key={airbnbGroupsKey} language={language} token={token} onChanged={() => fetchAllData(true)} />
                  </div>
                )}
                {loading ? (
                  <p>{language === 'pt' ? 'Carregando...' : 'Loading...'}</p>
                ) : filteredAppointments.length === 0 ? (
                  <p className="text-gray-600">{language === 'pt' ? 'Nenhum agendamento encontrado' : 'No appointments found'}</p>
                ) : (
                  <div className="space-y-4">
                    {filteredAppointments.map((appt, index) => (
                      <div key={appt.id} className="border border-blue-200 rounded-xl p-5 bg-gradient-to-r from-blue-50 to-white shadow-sm hover:shadow-md transition-shadow" data-testid={`appointment-${index}`}>
                        {/* Header */}
                        <div className="flex justify-between items-start mb-4 pb-3 border-b border-blue-100">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                              <Calendar size={24} className="text-blue-600" />
                            </div>
                            <div>
                              <h3 className="font-heading font-semibold text-lg text-gray-900">{appt.name}</h3>
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <Clock size={12} />
                                {language === 'pt' ? 'Criado em' : 'Created on'}: {formatDate(appt.created_at)}
                              </p>
                            </div>
                          </div>
                          <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(appt.status)}`}>
                            {appt.status === 'pending' && <AlertCircle size={12} />}
                            {appt.status === 'in_progress' && <CheckCircle size={12} />}
                            {appt.status === 'completed' && <CheckCircle size={12} />}
                            {appt.status === 'pending' && (language === 'pt' ? 'Pendente' : 'Pending')}
                            {appt.status === 'in_progress' && (language === 'pt' ? 'Confirmado' : 'Confirmed')}
                            {appt.status === 'completed' && (language === 'pt' ? 'Concluído' : 'Completed')}
                          </span>
                        </div>
                        
                        {/* Appointment Date/Time - Highlighted */}
                        <div className="bg-blue-100 rounded-lg p-3 mb-4 flex items-center justify-center gap-4">
                          <div className="text-center">
                            <p className="text-xs text-blue-600 font-medium">{language === 'pt' ? 'DATA' : 'DATE'}</p>
                            <p className="text-lg font-bold text-blue-800">{appt.preferred_date}</p>
                          </div>
                          <div className="w-px h-10 bg-blue-300"></div>
                          <div className="text-center">
                            <p className="text-xs text-blue-600 font-medium">{language === 'pt' ? 'HORA' : 'TIME'}</p>
                            <p className="text-lg font-bold text-blue-800">{appt.preferred_time}</p>
                          </div>
                        </div>
                        
                        {/* Contact Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                          <div className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-lg">
                            <Phone size={16} className="text-blue-600" />
                            <a href={`tel:${appt.phone}`} className="text-gray-700 hover:text-blue-600">{appt.phone}</a>
                          </div>
                          <div className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-lg">
                            <Mail size={16} className="text-blue-600" />
                            <a href={`mailto:${appt.email}`} className="text-gray-700 hover:text-blue-600 truncate">{appt.email}</a>
                          </div>
                          <div className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-lg">
                            <Briefcase size={16} className="text-blue-600" />
                            <span className="text-gray-700">{appt.service_type}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-lg md:col-span-2 lg:col-span-3">
                            <MapPin size={16} className="text-blue-600 flex-shrink-0" />
                            <span className="text-gray-700">{appt.address}</span>
                          </div>
                          
                          {/* Estimated Price and Hours */}
                          {(appt.estimated_price || appt.estimated_time) && (
                            <>
                              {appt.estimated_price && (
                                <div className="flex items-center gap-2 text-sm bg-emerald-50 px-3 py-2 rounded-lg border border-emerald-200">
                                  <DollarSign size={16} className="text-emerald-600" />
                                  <span className="text-emerald-700 font-medium">
                                    €{parseFloat(appt.estimated_price).toFixed(2)}
                                  </span>
                                  {appt.discount_amount > 0 && (
                                    <span className="text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-testid="appt-discount-badge">
                                      −€{parseFloat(appt.discount_amount).toFixed(2)} {appt.discount_type === 'percent' ? `(${appt.discount_value}%)` : ''}
                                    </span>
                                  )}
                                  {appt.source === 'airbnb' && (
                                    <span className="text-xs text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full">Airbnb</span>
                                  )}
                                  <span className="text-xs text-emerald-500">
                                    {language === 'pt' ? 'Preço Est.' : 'Est. Price'}
                                  </span>
                                </div>
                              )}
                              {appt.estimated_time && (
                                <div className="flex items-center gap-2 text-sm bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                                  <Clock size={16} className="text-blue-600" />
                                  <span className="text-blue-700 font-medium">
                                    {appt.estimated_time}
                                  </span>
                                  <span className="text-xs text-blue-500">
                                    {language === 'pt' ? 'Tempo Est.' : 'Est. Time'}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        
                        {/* Notes */}
                        {appt.notes && (
                          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r-lg mb-4">
                            <p className="text-xs font-medium text-yellow-700 mb-1 flex items-center gap-1">
                              <MessageSquare size={12} />
                              {language === 'pt' ? 'Notas do Cliente' : 'Client Notes'}
                            </p>
                            <p className="text-sm text-gray-700">{appt.notes}</p>
                          </div>
                        )}
                        
                        {/* Photos */}
                        {appt.photos && appt.photos.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                              <Image size={12} />
                              {language === 'pt' ? 'Fotos do Local' : 'Location Photos'} ({appt.photos.length})
                            </p>
                            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                              {appt.photos.map((photo, idx) => (
                                <div 
                                  key={idx} 
                                  className="relative group cursor-pointer"
                                  onClick={() => openImageModal(photo, `${appt.name} - ${language === 'pt' ? 'Foto' : 'Photo'} ${idx + 1}`)}
                                  data-testid={`appointment-photo-${idx}`}
                                >
                                  <img
                                    src={photo}
                                    alt={`${language === 'pt' ? 'Foto' : 'Photo'} ${idx + 1}`}
                                    className="w-full h-16 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity"
                                    onError={(e) => e.target.src = 'https://via.placeholder.com/100x80?text=Erro'}
                                  />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center pointer-events-none">
                                    <ExternalLink size={14} className="text-white" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Status Update */}
                        <div className="flex flex-col gap-2 pt-3 border-t border-blue-100">
                          {/* Show who updated - Only for Super Admin */}
                          {isSuperAdmin() && appt.status_updated_by && (
                            <div className="flex items-center gap-2 text-xs bg-yellow-50 border border-yellow-200 px-3 py-2 rounded-lg">
                              <UserCheck size={14} className="text-yellow-600" />
                              <span className="text-yellow-800">
                                {language === 'pt' ? 'Atualizado por' : 'Updated by'}: <strong>{appt.status_updated_by_name || appt.status_updated_by}</strong>
                                {appt.status_updated_at && (
                                  <span className="text-yellow-600 ml-2">
                                    ({new Date(appt.status_updated_at).toLocaleString('pt-PT')})
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                          
                          {/* Staff Assignment Info - shown if assigned */}
                          {appt.assigned_staff_id && (
                            <div className="flex items-center gap-2 text-xs bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
                              <UserCheck size={14} className="text-green-600" />
                              <span className="text-green-800">
                                {language === 'pt' ? 'Atribuído a' : 'Assigned to'}: <strong>{appt.assigned_staff_name}</strong>
                              </span>
                            </div>
                          )}
                          
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">{language === 'pt' ? 'Alterar estado:' : 'Change status:'}</span>
                              <select
                                value={appt.status}
                                onChange={(e) => updateAppointmentStatus(appt.id, e.target.value)}
                                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                              >
                                <option value="pending">{language === 'pt' ? '⏳ Pendente' : '⏳ Pending'}</option>
                                <option value="scheduled">{language === 'pt' ? '📅 Agendado' : '📅 Scheduled'}</option>
                                <option value="in_progress">{language === 'pt' ? '✅ Confirmado' : '✅ Confirmed'}</option>
                                <option value="completed">{language === 'pt' ? '🎉 Concluído' : '🎉 Completed'}</option>
                              </select>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                onClick={() => setProfileClient({ name: appt.name, phone: appt.phone, email: appt.email })}
                                className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors flex items-center gap-1"
                                data-testid={`client-profile-${index}`}
                                title={language === 'pt' ? 'Ficha do cliente' : 'Client profile'}
                              >
                                <Users size={14} />
                                {language === 'pt' ? 'Ficha' : 'Profile'}
                              </button>
                              <button
                                onClick={() => openEditAppointment(appt)}
                                className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors flex items-center gap-1"
                                data-testid={`edit-appointment-${index}`}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                                {language === 'pt' ? 'Editar' : 'Edit'}
                              </button>
                              <button
                                onClick={() => openDeleteModal('appointment', appt.id, appt.name)}
                                className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors flex items-center gap-1"
                                data-testid={`delete-appointment-${index}`}
                              >
                                <Trash2 size={14} />
                                {language === 'pt' ? 'Excluir' : 'Delete'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Completed Services Tab */}
            {activeTab === 'completed' && (
              <div data-testid="completed-section" className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-heading font-semibold text-gray-900">
                      {language === 'pt' ? 'Serviços Concluídos' : 'Completed Services'}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {language === 'pt' 
                        ? 'Histórico de todos os serviços finalizados com horas trabalhadas.' 
                        : 'History of all completed services with worked hours.'}
                    </p>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm border">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                      <CheckCircle size={16} className="text-emerald-500" />
                      {language === 'pt' ? 'Total Concluídos' : 'Total Completed'}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {appointments.filter(a => a.status === 'completed').length}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                      <Clock size={16} className="text-blue-500" />
                      {language === 'pt' ? 'Horas Trabalhadas' : 'Hours Worked'}
                    </div>
                    <div className="text-2xl font-bold text-blue-600">
                      {(workSessions.filter(s => s.action === 'finish').reduce((sum, s) => sum + (s.elapsed_seconds || 0), 0) / 3600).toFixed(1)}h
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                      <Users size={16} className="text-purple-500" />
                      {language === 'pt' ? 'Funcionários Ativos' : 'Active Staff'}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {new Set(workSessions.map(s => s.staff_id)).size}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                      <DollarSign size={16} className="text-emerald-500" />
                      {language === 'pt' ? 'Este Mês' : 'This Month'}
                    </div>
                    <div className="text-2xl font-bold text-emerald-600">
                      {appointments.filter(a => {
                        if (a.status !== 'completed') return false;
                        const completedDate = new Date(a.status_updated_at || a.updated_at || a.created_at);
                        const now = new Date();
                        return completedDate.getMonth() === now.getMonth() && completedDate.getFullYear() === now.getFullYear();
                      }).length}
                    </div>
                  </div>
                </div>

                {/* Completed Services List */}
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <div className="p-4 border-b bg-gray-50">
                    <h3 className="font-semibold text-gray-900">
                      {language === 'pt' ? 'Lista de Serviços Concluídos' : 'Completed Services List'}
                    </h3>
                  </div>
                  
                  {appointments.filter(a => a.status === 'completed').length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <CheckCircle size={48} className="mx-auto mb-4 text-gray-300" />
                      <p>{language === 'pt' ? 'Nenhum serviço concluído ainda.' : 'No completed services yet.'}</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {appointments
                        .filter(a => a.status === 'completed')
                        .sort((a, b) => new Date(b.status_updated_at || b.updated_at || 0) - new Date(a.status_updated_at || a.updated_at || 0))
                        .map(appointment => {
                          const workedData = getJobWorkedHours(appointment.id);
                          const staffNames = getJobStaffNames(appointment);
                          
                          return (
                            <div key={appointment.id} className="p-4 hover:bg-gray-50 transition-colors">
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                {/* Service Info */}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      appointment.service_type === 'Deep Cleaning' 
                                        ? 'bg-purple-100 text-purple-700'
                                        : 'bg-blue-100 text-blue-700'
                                    }`}>
                                      {appointment.service_type || 'Limpeza'}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {new Date(appointment.status_updated_at || appointment.updated_at || appointment.created_at).toLocaleDateString('pt-PT')}
                                    </span>
                                  </div>
                                  <h4 className="font-medium text-gray-900">{appointment.name}</h4>
                                  <p className="text-sm text-gray-500 flex items-center gap-1">
                                    <MapPin size={14} />
                                    {appointment.address || 'Sem endereço'}
                                  </p>
                                  {/* Estimated price and time */}
                                  {(appointment.estimated_price || appointment.estimated_time) && (
                                    <div className="flex items-center gap-3 mt-2">
                                      {appointment.estimated_price && (
                                        <span className="flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded">
                                          <DollarSign size={12} />
                                          €{parseFloat(appointment.estimated_price).toFixed(2)}
                                        </span>
                                      )}
                                      {appointment.estimated_time && (
                                        <span className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                          <Clock size={12} />
                                          {appointment.estimated_time} {language === 'pt' ? 'est.' : 'est.'}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Staff Info */}
                                <div className="flex-shrink-0">
                                  <p className="text-xs text-gray-500 mb-1">
                                    {language === 'pt' ? 'Funcionários' : 'Staff'}
                                  </p>
                                  <div className="flex flex-wrap gap-1">
                                    {staffNames.length > 0 ? (
                                      staffNames.map((name, idx) => (
                                        <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                          {name}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-xs text-gray-400">
                                        {language === 'pt' ? 'Não atribuído' : 'Unassigned'}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Hours Info */}
                                <div className="flex-shrink-0 text-right">
                                  <p className="text-xs text-gray-500 mb-1">
                                    {language === 'pt' ? 'Horas Trabalhadas' : 'Hours Worked'}
                                  </p>
                                  <div className="flex items-center justify-end gap-2">
                                    <span className={`text-xl font-bold ${workedData.hours > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                                      {workedData.hours.toFixed(1)}h
                                    </span>
                                    {workedData.hours > 0 && (
                                      <div className="flex gap-1">
                                        {workedData.hasTimer && (
                                          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px]">
                                            Timer
                                          </span>
                                        )}
                                        {workedData.hasManual && (
                                          <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px]">
                                            Manual
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {workedData.hours === 0 && (
                                    <p className="text-[10px] text-amber-500">
                                      {language === 'pt' ? 'Sem horas registadas' : 'No hours recorded'}
                                    </p>
                                  )}
                                  <button onClick={() => setEditingCompleted(appointment)} data-testid={`completed-edit-${appointment.id}`}
                                    className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-[#1a5c3a] text-[#1a5c3a] hover:bg-[#1a5c3a]/5 font-medium">
                                    <Edit size={12} /> {language === 'pt' ? 'Editar' : 'Edit'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && <AdminGalleryManagement />}

            {activeTab === 'users' && <AdminUserManagement language={language} />}
            {activeTab === 'whatsapp' && <AdminWhatsApp language={language} token={token} onCountChange={setReminderCount} />}
            {activeTab === 'reviews' && <AdminGoogleReviews language={language} token={token} />}

            {/* Calendar Tab */}
            {activeTab === 'calendar' && (
              <div data-testid="calendar-section">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-6">
                  <div className="min-w-0">
                    <h2 className="text-xl font-heading font-semibold">
                      {language === 'pt' ? 'Calendário de Serviços' : 'Services Calendar'}
                    </h2>
                    <p className="text-sm text-gray-600 hidden sm:block">
                      {language === 'pt' 
                        ? 'Visualize todos os serviços agendados e funcionários atribuídos.' 
                        : 'View all scheduled services and assigned staff.'}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-600" data-testid="calendar-legend">
                      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-rose-500"></span> Airbnb</span>
                      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-gray-300"></span> {language === 'pt' ? 'Agendado' : 'Scheduled'}</span>
                      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-500"></span> {language === 'pt' ? 'Em curso' : 'In progress'}</span>
                      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-green-500"></span> {language === 'pt' ? 'Concluído' : 'Completed'}</span>
                      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500"></span> {language === 'pt' ? 'Cancelado' : 'Cancelled'}</span>
                    </div>
                  </div>
                  
                  {/* Export Buttons */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => {
                        // Generate ICS file for Google Calendar
                        const icsContent = [
                          'BEGIN:VCALENDAR',
                          'VERSION:2.0',
                          'PRODID:-//Sancur Services//Calendar Export//EN',
                          'CALSCALE:GREGORIAN',
                          'METHOD:PUBLISH',
                          ...appointments.map(appt => {
                            const startDate = appt.date || appt.preferred_date || new Date().toISOString().split('T')[0];
                            const startTime = (appt.time || appt.preferred_time || '09:00').replace(':', '');
                            const dtStart = startDate.replace(/-/g, '') + 'T' + startTime + '00';
                            const endHour = parseInt(startTime.substring(0,2)) + 3;
                            const dtEnd = startDate.replace(/-/g, '') + 'T' + String(endHour).padStart(2,'0') + startTime.substring(2) + '00';
                            return [
                              'BEGIN:VEVENT',
                              `UID:${appt.id}@sancur.ie`,
                              `DTSTART:${dtStart}`,
                              `DTEND:${dtEnd}`,
                              `SUMMARY:${appt.service_type || 'Cleaning'} - ${appt.name || 'Client'}`,
                              `LOCATION:${appt.address || ''}`,
                              `DESCRIPTION:Client: ${appt.name}\\nPhone: ${appt.phone || 'N/A'}\\nStaff: ${appt.assigned_staff_name || 'Unassigned'}`,
                              'END:VEVENT'
                            ].join('\n');
                          }),
                          'END:VCALENDAR'
                        ].join('\n');
                        
                        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `sancur-calendar-${new Date().toISOString().split('T')[0]}.ics`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                      }}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap"
                      data-testid="export-google-calendar"
                    >
                      <Calendar size={16} />
                      Google<span className="hidden sm:inline">&nbsp;Calendar</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        // Generate PDF
                        const printContent = `
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <title>Sancur - ${language === 'pt' ? 'Calendário de Serviços' : 'Services Calendar'}</title>
                            <style>
                              body { font-family: Arial, sans-serif; padding: 20px; }
                              h1 { color: #1a5c3a; border-bottom: 2px solid #1a5c3a; padding-bottom: 10px; }
                              .date-group { margin-bottom: 20px; page-break-inside: avoid; }
                              .date-header { background: #1a5c3a; color: white; padding: 10px; border-radius: 5px; margin-bottom: 10px; }
                              .appointment { border: 1px solid #ddd; padding: 12px; margin-bottom: 8px; border-radius: 5px; border-left: 4px solid #1a5c3a; }
                              .appointment-header { font-weight: bold; font-size: 14px; }
                              .appointment-details { font-size: 12px; color: #666; margin-top: 5px; }
                              .staff-badge { display: inline-block; background: #e9d5ff; color: #7c3aed; padding: 2px 8px; border-radius: 10px; font-size: 11px; }
                              .unassigned { background: #fef3c7; color: #d97706; }
                              .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 11px; color: #999; }
                            </style>
                          </head>
                          <body>
                            <h1>🗓️ ${language === 'pt' ? 'Calendário de Serviços - Sancur' : 'Services Calendar - Sancur'}</h1>
                            <p>${language === 'pt' ? 'Exportado em' : 'Exported on'}: ${new Date().toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-IE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                            
                            ${Object.entries(
                              appointments.reduce((groups, appt) => {
                                const date = appt.date || appt.preferred_date || 'No date';
                                if (!groups[date]) groups[date] = [];
                                groups[date].push(appt);
                                return groups;
                              }, {})
                            )
                            .sort(([a], [b]) => new Date(a) - new Date(b))
                            .map(([date, dayAppts]) => `
                              <div class="date-group">
                                <div class="date-header">
                                  📅 ${new Date(date).toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-IE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                  - ${dayAppts.length} ${language === 'pt' ? 'serviço(s)' : 'service(s)'}
                                </div>
                                ${dayAppts.map(appt => `
                                  <div class="appointment">
                                    <div class="appointment-header">${appt.name || 'N/A'} - ${appt.service_type || 'Cleaning'}</div>
                                    <div class="appointment-details">
                                      🕐 ${appt.time || appt.preferred_time || '--:--'} | 
                                      📍 ${appt.address || 'No address'} |
                                      📞 ${appt.phone || 'N/A'}
                                    </div>
                                    <div style="margin-top: 5px;">
                                      <span class="staff-badge ${!appt.assigned_staff_name ? 'unassigned' : ''}">
                                        👤 ${appt.assigned_staff_name || (language === 'pt' ? 'Sem funcionário' : 'Unassigned')}
                                      </span>
                                    </div>
                                  </div>
                                `).join('')}
                              </div>
                            `).join('')}
                            
                            <div class="footer">
                              Sancur Property Services | Dublin, Ireland | www.sancur.ie
                            </div>
                          </body>
                          </html>
                        `;
                        
                        const printWindow = window.open('', '_blank');
                        printWindow.document.write(printContent);
                        printWindow.document.close();
                        printWindow.print();
                      }}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium whitespace-nowrap"
                      data-testid="export-pdf"
                    >
                      <FileText size={16} />
                      PDF
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mb-4" data-testid="calendar-view-toggle">
                  {[{ id: 'month', label: language === 'pt' ? 'Mês completo' : 'Full month' }, { id: 'list', label: language === 'pt' ? 'Lista de serviços' : 'Jobs list' }].map(v => (
                    <button key={v.id} onClick={() => setCalendarView(v.id)} data-testid={`calendar-view-${v.id}`}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${calendarView === v.id ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                      {v.label}
                    </button>
                  ))}
                </div>

                {calendarView === 'month' && (
                  <AdminMonthCalendar
                    appointments={appointments}
                    language={language}
                    onCreate={(dateKey) => {
                      setEditingAppointment(null);
                      setNewAppointment(prev => ({ ...prev, preferred_date: dateKey }));
                      setShowNewAppointmentModal(true);
                    }}
                    onMove={async (appt, newDate) => {
                      const fmtD = (d) => new Date(d + 'T00:00:00').toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-IE', { weekday: 'short', day: '2-digit', month: 'short' });
                      const oldDate = appt.preferred_date || appt.date;
                      if (!window.confirm(language === 'pt'
                        ? `Reagendar "${appt.name}" de ${fmtD(oldDate)} para ${fmtD(newDate)}? O staff atribuído será avisado.`
                        : `Reschedule "${appt.name}" from ${fmtD(oldDate)} to ${fmtD(newDate)}? Assigned staff will be notified.`)) return;
                      try {
                        await axios.put(`${API}/admin/appointments/${appt.id}`, { preferred_date: newDate, date: newDate }, { headers: { Authorization: `Bearer ${token}` } });
                        await fetchAllData(true);
                      } catch (e) {
                        alert(language === 'pt' ? 'Erro ao reagendar.' : 'Failed to reschedule.');
                      }
                    }}
                    onProfile={(appt) => setProfileClient({ name: appt.name, phone: appt.phone, email: appt.email })}
                    onCancel={async (appt) => {
                      if (!window.confirm(language === 'pt'
                        ? `Cancelar o serviço de "${appt.name}" em ${appt.preferred_date || appt.date}? O staff atribuído será avisado.`
                        : `Cancel "${appt.name}" on ${appt.preferred_date || appt.date}? Assigned staff will be notified.`)) return;
                      try {
                        await axios.patch(`${API}/admin/appointments/${appt.id}`, { status: 'cancelled' }, { headers: { Authorization: `Bearer ${token}` } });
                        await fetchAllData(true);
                      } catch (e) {
                        alert(language === 'pt' ? 'Erro ao cancelar.' : 'Failed to cancel.');
                      }
                    }}
                  />
                )}

                {/* Calendar Grid */}
                {calendarView === 'list' && <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  {/* Calendar Header */}
                  <div className="bg-primary text-white p-4">
                    <h3 className="font-semibold text-lg">
                      {new Date().toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-IE', { month: 'long', year: 'numeric' })}
                    </h3>
                  </div>
                  
                  {/* Appointments List by Date */}
                  <div className="divide-y divide-gray-100">
                    {appointments.length === 0 ? (
                      <div className="text-center py-12">
                        <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">
                          {language === 'pt' ? 'Nenhum agendamento encontrado.' : 'No appointments found.'}
                        </p>
                      </div>
                    ) : (
                      // Group appointments by date
                      Object.entries(
                        appointments.reduce((groups, appt) => {
                          const date = appt.date || appt.preferred_date || 'Sem data';
                          if (!groups[date]) groups[date] = [];
                          groups[date].push(appt);
                          return groups;
                        }, {})
                      )
                      .sort(([a], [b]) => new Date(a) - new Date(b))
                      .map(([date, dayAppointments]) => (
                        <div key={date} className="p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex flex-col items-center justify-center">
                              <span className="text-xs text-primary font-medium">
                                {new Date(date).toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-IE', { weekday: 'short' })}
                              </span>
                              <span className="text-lg font-bold text-primary">
                                {new Date(date).getDate()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {new Date(date).toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-IE', { weekday: 'long', day: 'numeric', month: 'long' })}
                              </p>
                              <p className="text-sm text-gray-500">
                                {dayAppointments.length} {language === 'pt' ? 'serviço(s)' : 'service(s)'}
                              </p>
                            </div>
                          </div>
                          
                          <div className="space-y-2 ml-15">
                            {dayAppointments.map((appt, idx) => (
                              <div 
                                key={appt.id || idx}
                                className={`p-3 rounded-lg border-l-4 ${
                                  appt.status === 'completed' ? 'bg-green-50 border-green-500' :
                                  appt.status === 'in_progress' ? 'bg-blue-50 border-blue-500' :
                                  appt.status === 'cancelled' ? 'bg-red-50 border-red-500' :
                                  appt.source === 'airbnb' ? 'bg-rose-50 border-rose-500' :
                                  'bg-gray-50 border-gray-300'
                                }`}
                                data-testid={appt.source === 'airbnb' ? 'calendar-appt-airbnb' : 'calendar-appt'}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-gray-900 flex items-center gap-2">
                                      {appt.name}
                                      {appt.source === 'airbnb' && (
                                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-500 text-white">🏡 Airbnb</span>
                                      )}
                                    </p>
                                    <p className="text-sm text-gray-600">{appt.service_type}</p>
                                    <p className="text-xs text-gray-500">
                                      {appt.time || appt.preferred_time || '--:--'} • {appt.address || 'Sem endereço'}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    {appt.assigned_staff_name ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                                        <UserCheck size={12} />
                                        {appt.assigned_staff_name}
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                                        <AlertCircle size={12} />
                                        {language === 'pt' ? 'Sem funcionário' : 'Unassigned'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>}
              </div>
            )}

            {/* Client History Tab */}
            {activeTab === 'clients' && (
              <AdminClientsHistory token={token} language={language} onOpenProfile={(c) => setProfileClient({ name: c.name, phone: c.phone, email: c.email })} />
            )}

            {/* Staff Tab */}
            {activeTab === 'staff' && (
              <AdminStaffManagement 
                token={token} 
                language={language}
                contacts={contacts}
                appointments={appointments}
                onRefresh={() => fetchAllData(true)}
              />
            )}

            {/* Hours Report Tab */}
            {activeTab === 'hours' && (
              <AdminHoursReport 
                token={token} 
                language={language}
                staff={staff}
                appointments={appointments}
              />
            )}

            {/* Finance Tab */}
            {activeTab === 'finance' && (
              <div data-testid="finance-section" className="space-y-6">
                {!financeUnlocked ? (
                  /* Password Protection Modal */
                  <div className="max-w-md mx-auto mt-12">
                    <div className="bg-white rounded-2xl shadow-lg border p-8 text-center">
                      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Lock size={32} className="text-amber-600" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900 mb-2">
                        {language === 'pt' ? 'Área Restrita' : 'Restricted Area'}
                      </h2>
                      <p className="text-gray-600 mb-6">
                        {language === 'pt' 
                          ? 'Digite o PIN para aceder às finanças da empresa.' 
                          : 'Enter the PIN to access company finances.'}
                      </p>
                      <div className="space-y-4">
                        <input
                          type="password"
                          value={financePassword}
                          onChange={(e) => setFinancePassword(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleFinanceAccess()}
                          placeholder="PIN"
                          className="w-full px-4 py-3 text-center text-2xl tracking-widest border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          maxLength={6}
                          data-testid="finance-pin-input"
                        />
                        {financePasswordError && (
                          <p className="text-red-500 text-sm">{financePasswordError}</p>
                        )}
                        <button
                          onClick={handleFinanceAccess}
                          className="w-full py-3 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors"
                          data-testid="finance-unlock-btn"
                        >
                          {language === 'pt' ? 'Aceder' : 'Access'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Finance Dashboard */
                  <div className="space-y-6">
                    {/* Header with Filters */}
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                          <h2 className="text-xl font-heading font-semibold text-gray-900 flex items-center gap-2">
                            <TrendingUp className="text-amber-500" size={24} />
                            {language === 'pt' ? 'Finanças da Empresa' : 'Company Finance'}
                          </h2>
                          <p className="text-sm text-gray-600">
                            {language === 'pt' 
                              ? 'Lucro após pagamento de funcionários e custos' 
                              : 'Profit after staff payment and costs'}
                          </p>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openExpenseModal()}
                            className="px-3 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center gap-2"
                            data-testid="add-expense-btn"
                          >
                            <Plus size={16} />
                            {language === 'pt' ? 'Adicionar Custo' : 'Add Expense'}
                          </button>
                          {/* Change PIN button - only for super admin */}
                          {isSuperAdmin() && (
                            <button
                              onClick={() => setShowChangePinModal(true)}
                              className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                              title={language === 'pt' ? 'Alterar PIN' : 'Change PIN'}
                              data-testid="change-pin-btn"
                            >
                              <Shield size={20} />
                            </button>
                          )}
                          <button
                            onClick={() => setFinanceUnlocked(false)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title={language === 'pt' ? 'Bloquear' : 'Lock'}
                          >
                            <Lock size={20} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Period Filter Row */}
                      <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-xl border">
                        <span className="text-sm font-medium text-gray-600">
                          {language === 'pt' ? 'Período:' : 'Period:'}
                        </span>
                        {[7, 15, 30].map(days => (
                          <button
                            key={days}
                            onClick={() => {
                              setFinancePeriod(days);
                              setUseCustomDates(false);
                            }}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                              financePeriod === days && !useCustomDates
                                ? 'bg-amber-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {days} {language === 'pt' ? 'dias' : 'days'}
                          </button>
                        ))}
                        <div className="h-6 w-px bg-gray-200 mx-2"></div>
                        <button
                          onClick={() => setUseCustomDates(!useCustomDates)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            useCustomDates
                              ? 'bg-amber-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {language === 'pt' ? 'Personalizado' : 'Custom'}
                        </button>
                        
                        {useCustomDates && (
                          <div className="flex items-center gap-2 ml-2">
                            <input
                              type="date"
                              value={customDateRange.start}
                              onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                              className="px-3 py-2 border rounded-lg text-sm"
                            />
                            <span className="text-gray-400">→</span>
                            <input
                              type="date"
                              value={customDateRange.end}
                              onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                              className="px-3 py-2 border rounded-lg text-sm"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Summary Cards + Export */}
                    {(() => {
                      const financeData = calculateFinanceData();
                      
                      // PDF Export Function
                      const exportFinancePDF = () => {
                        const periodText = useCustomDates && customDateRange.start && customDateRange.end
                          ? `${new Date(customDateRange.start).toLocaleDateString('pt-PT')} a ${new Date(customDateRange.end).toLocaleDateString('pt-PT')}`
                          : `Últimos ${financePeriod} dias`;
                        
                        const jobsRows = financeData.jobs.map(job => `
                          <tr>
                            <td>${new Date(job.status_updated_at || job.updated_at).toLocaleDateString('pt-PT')}</td>
                            <td>${job.service_type || 'Limpeza'}</td>
                            <td>${job.name || '-'}</td>
                            <td>${job.staffNames?.join(', ') || '-'}</td>
                            <td class="text-right green">€${job.revenue.toFixed(2)}</td>
                            <td class="text-right orange">€${job.staffCost.toFixed(2)}</td>
                            <td class="text-right amber">€${job.profit.toFixed(2)}</td>
                          </tr>
                        `).join('');
                        
                        const expensesRows = financeData.expenses.map(exp => `
                          <tr>
                            <td>${new Date(exp.date).toLocaleDateString('pt-PT')}</td>
                            <td>${exp.name}</td>
                            <td>${exp.category}</td>
                            <td>${exp.expense_type === 'fixed' ? 'Fixo' : 'Serviço'}</td>
                            <td class="text-right red">€${exp.amount.toFixed(2)}</td>
                          </tr>
                        `).join('');
                        
                        const expensesSection = financeData.expenses.length > 0 ? `
                          <h2>Custos Extras</h2>
                          <table>
                            <thead>
                              <tr>
                                <th>Data</th>
                                <th>Descrição</th>
                                <th>Categoria</th>
                                <th>Tipo</th>
                                <th class="text-right">Valor</th>
                              </tr>
                            </thead>
                            <tbody>${expensesRows}</tbody>
                          </table>
                        ` : '';
                        
                        const printWindow = window.open('', '_blank');
                        printWindow.document.write(`
                          <html>
                            <head>
                              <title>Sancur - Relatório Financeiro</title>
                              <style>
                                body { font-family: Arial, sans-serif; padding: 20px; }
                                h1 { color: #d97706; margin-bottom: 5px; }
                                h2 { color: #374151; margin-top: 30px; }
                                .subtitle { color: #6b7280; margin-bottom: 20px; }
                                .summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 20px 0; }
                                .card { padding: 15px; border: 1px solid #e5e7eb; border-radius: 8px; }
                                .card-label { font-size: 12px; color: #6b7280; margin-bottom: 5px; }
                                .card-value { font-size: 20px; font-weight: bold; }
                                .green { color: #16a34a; }
                                .orange { color: #ea580c; }
                                .red { color: #dc2626; }
                                .amber { color: #d97706; }
                                table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                                th, td { padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb; }
                                th { background: #f9fafb; font-weight: 600; }
                                .text-right { text-align: right; }
                                .total-row { background: #fef3c7; font-weight: bold; }
                                @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
                              </style>
                            </head>
                            <body>
                              <h1>Sancur Property Services</h1>
                              <p class="subtitle">Relatório Financeiro - ${periodText}</p>
                              
                              <div class="summary">
                                <div class="card">
                                  <div class="card-label">Serviços Concluídos</div>
                                  <div class="card-value">${financeData.totalJobs}</div>
                                </div>
                                <div class="card">
                                  <div class="card-label">Faturação Total</div>
                                  <div class="card-value green">€${financeData.totalRevenue.toFixed(2)}</div>
                                </div>
                                <div class="card">
                                  <div class="card-label">Custo Staff</div>
                                  <div class="card-value orange">€${financeData.totalStaffCost.toFixed(2)}</div>
                                </div>
                                <div class="card">
                                  <div class="card-label">Custos Extras</div>
                                  <div class="card-value red">€${financeData.totalExpenses.toFixed(2)}</div>
                                </div>
                                <div class="card">
                                  <div class="card-label">Lucro Líquido</div>
                                  <div class="card-value amber">€${financeData.totalProfit.toFixed(2)}</div>
                                </div>
                                <div class="card">
                                  <div class="card-label">Margem de Lucro</div>
                                  <div class="card-value">${financeData.avgProfitMargin.toFixed(1)}%</div>
                                </div>
                              </div>
                              
                              <h2>Serviços</h2>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Data</th>
                                    <th>Serviço</th>
                                    <th>Cliente</th>
                                    <th>Staff</th>
                                    <th class="text-right">Faturação</th>
                                    <th class="text-right">Custo</th>
                                    <th class="text-right">Lucro</th>
                                  </tr>
                                </thead>
                                <tbody>${jobsRows}</tbody>
                              </table>
                              
                              ${expensesSection}
                              
                              <h2>Resumo Final</h2>
                              <table>
                                <tbody>
                                  <tr><td>Faturação Total</td><td class="text-right green">€${financeData.totalRevenue.toFixed(2)}</td></tr>
                                  <tr><td>(-) Custo Staff</td><td class="text-right orange">€${financeData.totalStaffCost.toFixed(2)}</td></tr>
                                  <tr><td>(-) Custos Extras</td><td class="text-right red">€${financeData.totalExpenses.toFixed(2)}</td></tr>
                                  <tr class="total-row"><td><strong>Lucro Líquido</strong></td><td class="text-right amber"><strong>€${financeData.totalProfit.toFixed(2)}</strong></td></tr>
                                </tbody>
                              </table>
                              
                              <p style="margin-top: 40px; color: #9ca3af; font-size: 12px; text-align: center;">
                                Gerado em ${new Date().toLocaleString('pt-PT')} | Sancur Property Services
                              </p>
                            </body>
                          </html>
                        `);
                        printWindow.document.close();
                        printWindow.print();
                      };
                      
                      return (
                        <>
                          {/* Summary Cards */}
                          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4" id="finance-print-content">
                            <div className="bg-white p-4 rounded-xl shadow-sm border">
                              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                                <FileText size={16} className="text-blue-500" />
                                {language === 'pt' ? 'Serviços' : 'Services'}
                              </div>
                              <div className="text-2xl font-bold text-gray-900">{financeData.totalJobs}</div>
                            </div>
                            <div className="bg-white p-4 rounded-xl shadow-sm border">
                              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                                <DollarSign size={16} className="text-green-500" />
                                {language === 'pt' ? 'Faturação' : 'Revenue'}
                              </div>
                              <div className="text-2xl font-bold text-green-600">€{financeData.totalRevenue.toFixed(2)}</div>
                            </div>
                            <div className="bg-white p-4 rounded-xl shadow-sm border">
                              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                                <Users size={16} className="text-orange-500" />
                                {language === 'pt' ? 'Custo Staff' : 'Staff Cost'}
                              </div>
                              <div className="text-2xl font-bold text-orange-600">€{financeData.totalStaffCost.toFixed(2)}</div>
                            </div>
                            <div className="bg-white p-4 rounded-xl shadow-sm border">
                              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                                <Briefcase size={16} className="text-red-500" />
                                {language === 'pt' ? 'Custos Extras' : 'Extra Costs'}
                              </div>
                              <div className="text-2xl font-bold text-red-600">€{financeData.totalExpenses.toFixed(2)}</div>
                            </div>
                            <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-4 rounded-xl shadow-sm text-white">
                              <div className="flex items-center gap-2 text-amber-100 text-sm mb-1">
                                <TrendingUp size={16} />
                                {language === 'pt' ? 'Lucro Líquido' : 'Net Profit'}
                              </div>
                              <div className="text-2xl font-bold">€{financeData.totalProfit.toFixed(2)}</div>
                            </div>
                            <div className="bg-white p-4 rounded-xl shadow-sm border">
                              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                                <BarChart3 size={16} className="text-purple-500" />
                                {language === 'pt' ? 'Margem' : 'Margin'}
                              </div>
                              <div className={`text-2xl font-bold ${financeData.avgProfitMargin >= 30 ? 'text-green-600' : financeData.avgProfitMargin >= 15 ? 'text-amber-600' : 'text-red-600'}`}>
                                {financeData.avgProfitMargin.toFixed(1)}%
                              </div>
                            </div>
                          </div>

                          <FinanceAirbnbReceivables language={language} token={token} onOpenAirbnb={() => { setShowAirbnbGroups(true); setActiveTab('appointments'); }} />

                          <AdminInvoicesHistory language={language} token={token} />

                          {/* Export Button */}
                          <div className="flex justify-end">
                            <button
                              onClick={exportFinancePDF}
                              className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors flex items-center gap-2"
                              data-testid="export-finance-pdf"
                            >
                              <Download size={16} />
                              {language === 'pt' ? 'Exportar PDF' : 'Export PDF'}
                            </button>
                          </div>

                          {/* Services List */}
                          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                              <h3 className="font-semibold text-gray-900">
                                {language === 'pt' ? 'Detalhes por Serviço' : 'Service Details'}
                              </h3>
                              <span className="text-sm text-gray-500">
                                {useCustomDates && customDateRange.start && customDateRange.end
                                  ? `${new Date(customDateRange.start).toLocaleDateString('pt-PT')} - ${new Date(customDateRange.end).toLocaleDateString('pt-PT')}`
                                  : `${language === 'pt' ? 'Últimos' : 'Last'} ${financePeriod} ${language === 'pt' ? 'dias' : 'days'}`
                                }
                              </span>
                            </div>
                            
                            {financeData.jobs.length === 0 ? (
                              <div className="p-8 text-center text-gray-500">
                                <TrendingUp size={48} className="mx-auto mb-4 text-gray-300" />
                                <p>{language === 'pt' ? 'Nenhum serviço concluído neste período.' : 'No completed services in this period.'}</p>
                              </div>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead className="bg-gray-50 text-sm">
                                    <tr>
                                      <th className="px-4 py-3 text-left font-medium text-gray-500">{language === 'pt' ? 'Serviço' : 'Service'}</th>
                                      <th className="px-4 py-3 text-left font-medium text-gray-500">{language === 'pt' ? 'Cliente' : 'Client'}</th>
                                      <th className="px-4 py-3 text-left font-medium text-gray-500">{language === 'pt' ? 'Staff' : 'Staff'}</th>
                                      <th className="px-4 py-3 text-right font-medium text-gray-500">{language === 'pt' ? 'Faturação' : 'Revenue'}</th>
                                      <th className="px-4 py-3 text-right font-medium text-gray-500">{language === 'pt' ? 'Custo' : 'Cost'}</th>
                                      <th className="px-4 py-3 text-right font-medium text-gray-500">{language === 'pt' ? 'Lucro' : 'Profit'}</th>
                                      <th className="px-4 py-3 text-right font-medium text-gray-500">{language === 'pt' ? 'Margem' : 'Margin'}</th>
                                      <th className="px-4 py-3 text-center font-medium text-gray-500">{language === 'pt' ? 'Ações' : 'Actions'}</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y">
                                    {financeData.jobs.map(job => (
                                      <tr key={job.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                          <div className="flex items-center gap-2">
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                              job.service_type === 'Deep Cleaning' 
                                                ? 'bg-purple-100 text-purple-700'
                                                : 'bg-blue-100 text-blue-700'
                                            }`}>
                                              {job.service_type || 'Limpeza'}
                                            </span>
                                          </div>
                                          <p className="text-xs text-gray-500 mt-1">
                                            {new Date(job.status_updated_at || job.updated_at).toLocaleDateString('pt-PT')}
                                          </p>
                                        </td>
                                        <td className="px-4 py-3 font-medium text-gray-900">{job.name}</td>
                                        <td className="px-4 py-3">
                                          <div className="flex flex-wrap gap-1">
                                            {job.staffNames?.map((name, idx) => (
                                              <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                                {name}
                                              </span>
                                            )) || <span className="text-gray-400 text-xs">-</span>}
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 text-right font-medium text-green-600">€{job.revenue.toFixed(2)}</td>
                                        <td className="px-4 py-3 text-right font-medium text-orange-600">€{job.staffCost.toFixed(2)}</td>
                                        <td className="px-4 py-3 text-right font-bold text-amber-600">€{job.profit.toFixed(2)}</td>
                                        <td className="px-4 py-3 text-right">
                                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                                            job.profitMargin >= 30 ? 'bg-green-100 text-green-700' :
                                            job.profitMargin >= 15 ? 'bg-amber-100 text-amber-700' :
                                            'bg-red-100 text-red-700'
                                          }`}>
                                            {job.profitMargin.toFixed(1)}%
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          <button
                                            onClick={() => openEditServiceModal(job)}
                                            className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                                            title={language === 'pt' ? 'Editar valores' : 'Edit values'}
                                          >
                                            <FileText size={16} />
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot className="bg-amber-50 font-semibold">
                                    <tr>
                                      <td className="px-4 py-3" colSpan={3}>{language === 'pt' ? 'TOTAL SERVIÇOS' : 'TOTAL SERVICES'}</td>
                                      <td className="px-4 py-3 text-right text-green-600">€{financeData.totalRevenue.toFixed(2)}</td>
                                      <td className="px-4 py-3 text-right text-orange-600">€{financeData.totalStaffCost.toFixed(2)}</td>
                                      <td className="px-4 py-3 text-right text-amber-600">€{(financeData.totalRevenue - financeData.totalStaffCost).toFixed(2)}</td>
                                      <td className="px-4 py-3" colSpan={2}></td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            )}
                          </div>

                          {/* Expenses List */}
                          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                <Briefcase size={18} className="text-red-500" />
                                {language === 'pt' ? 'Custos Extras' : 'Extra Costs'}
                              </h3>
                              <button
                                onClick={() => openExpenseModal()}
                                className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors flex items-center gap-1"
                              >
                                <Plus size={14} />
                                {language === 'pt' ? 'Adicionar' : 'Add'}
                              </button>
                            </div>
                            
                            {financeData.expenses.length === 0 ? (
                              <div className="p-8 text-center text-gray-500">
                                <Briefcase size={48} className="mx-auto mb-4 text-gray-300" />
                                <p>{language === 'pt' ? 'Nenhum custo registado neste período.' : 'No costs recorded in this period.'}</p>
                                <button
                                  onClick={() => openExpenseModal()}
                                  className="mt-4 text-green-600 hover:text-green-700 font-medium"
                                >
                                  + {language === 'pt' ? 'Adicionar primeiro custo' : 'Add first cost'}
                                </button>
                              </div>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead className="bg-gray-50 text-sm">
                                    <tr>
                                      <th className="px-4 py-3 text-left font-medium text-gray-500">{language === 'pt' ? 'Data' : 'Date'}</th>
                                      <th className="px-4 py-3 text-left font-medium text-gray-500">{language === 'pt' ? 'Descrição' : 'Description'}</th>
                                      <th className="px-4 py-3 text-left font-medium text-gray-500">{language === 'pt' ? 'Categoria' : 'Category'}</th>
                                      <th className="px-4 py-3 text-left font-medium text-gray-500">{language === 'pt' ? 'Tipo' : 'Type'}</th>
                                      <th className="px-4 py-3 text-right font-medium text-gray-500">{language === 'pt' ? 'Valor' : 'Amount'}</th>
                                      <th className="px-4 py-3 text-center font-medium text-gray-500">{language === 'pt' ? 'Ações' : 'Actions'}</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y">
                                    {financeData.expenses.map(expense => (
                                      <tr key={expense.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                          {new Date(expense.date).toLocaleDateString('pt-PT')}
                                        </td>
                                        <td className="px-4 py-3 font-medium text-gray-900">
                                          {expense.name}
                                          {expense.notes && (
                                            <p className="text-xs text-gray-500 mt-0.5">{expense.notes}</p>
                                          )}
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                                            expense.category === 'rent' ? 'bg-purple-100 text-purple-700' :
                                            expense.category === 'supplies' ? 'bg-blue-100 text-blue-700' :
                                            expense.category === 'transport' ? 'bg-green-100 text-green-700' :
                                            expense.category === 'equipment' ? 'bg-orange-100 text-orange-700' :
                                            'bg-gray-100 text-gray-700'
                                          }`}>
                                            {expense.category === 'rent' ? (language === 'pt' ? 'Renda' : 'Rent') :
                                             expense.category === 'supplies' ? (language === 'pt' ? 'Produtos' : 'Supplies') :
                                             expense.category === 'transport' ? (language === 'pt' ? 'Transporte' : 'Transport') :
                                             expense.category === 'equipment' ? (language === 'pt' ? 'Equipamento' : 'Equipment') :
                                             (language === 'pt' ? 'Outro' : 'Other')}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                                            expense.expense_type === 'fixed' ? 'bg-amber-100 text-amber-700' : 'bg-cyan-100 text-cyan-700'
                                          }`}>
                                            {expense.expense_type === 'fixed' ? (language === 'pt' ? 'Fixo' : 'Fixed') : (language === 'pt' ? 'Serviço' : 'Service')}
                                          </span>
                                          {expense.recurring && (
                                            <span className="ml-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                              {language === 'pt' ? 'Recorrente' : 'Recurring'}
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-right font-bold text-red-600">€{expense.amount.toFixed(2)}</td>
                                        <td className="px-4 py-3 text-center">
                                          <div className="flex items-center justify-center gap-1">
                                            <button
                                              onClick={() => openExpenseModal(expense)}
                                              className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                                              title={language === 'pt' ? 'Editar' : 'Edit'}
                                            >
                                              <FileText size={16} />
                                            </button>
                                            <button
                                              onClick={() => handleDeleteExpense(expense.id)}
                                              className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                                              title={language === 'pt' ? 'Eliminar' : 'Delete'}
                                            >
                                              <Trash2 size={16} />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot className="bg-red-50 font-semibold">
                                    <tr>
                                      <td className="px-4 py-3" colSpan={4}>{language === 'pt' ? 'TOTAL CUSTOS' : 'TOTAL COSTS'}</td>
                                      <td className="px-4 py-3 text-right text-red-600">€{financeData.totalExpenses.toFixed(2)}</td>
                                      <td></td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            )}
                          </div>

                          {/* Final Summary */}
                          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 text-white">
                            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                              <TrendingUp size={20} className="text-amber-400" />
                              {language === 'pt' ? 'Resumo Final' : 'Final Summary'}
                            </h3>
                            <div className="space-y-3">
                              <div className="flex justify-between items-center py-2 border-b border-gray-700">
                                <span className="text-gray-300">{language === 'pt' ? 'Faturação Total' : 'Total Revenue'}</span>
                                <span className="font-semibold text-green-400">€{financeData.totalRevenue.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between items-center py-2 border-b border-gray-700">
                                <span className="text-gray-300">(-) {language === 'pt' ? 'Custo Staff' : 'Staff Cost'}</span>
                                <span className="font-semibold text-orange-400">€{financeData.totalStaffCost.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between items-center py-2 border-b border-gray-700">
                                <span className="text-gray-300">(-) {language === 'pt' ? 'Custos Extras' : 'Extra Costs'}</span>
                                <span className="font-semibold text-red-400">€{financeData.totalExpenses.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between items-center py-3 bg-amber-500/20 rounded-lg px-3 mt-2">
                                <span className="font-bold text-amber-300">{language === 'pt' ? 'LUCRO LÍQUIDO' : 'NET PROFIT'}</span>
                                <span className="font-bold text-2xl text-amber-400">€{financeData.totalProfit.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Change PIN Modal - Super Admin Only */}
      {showChangePinModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="change-pin-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-heading font-semibold text-gray-900 flex items-center gap-2">
                <Shield size={24} className="text-blue-500" />
                {language === 'pt' ? 'Alterar PIN de Finanças' : 'Change Finance PIN'}
              </h3>
              <button
                onClick={() => {
                  setShowChangePinModal(false);
                  setCurrentPinInput('');
                  setNewPinInput('');
                  setConfirmPinInput('');
                  setPinChangeError('');
                  setPinChangeSuccess('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <p className="text-gray-600 text-sm mb-4">
              {language === 'pt' 
                ? 'O novo PIN deve ter entre 4 e 6 dígitos numéricos.' 
                : 'New PIN must be 4-6 numeric digits.'}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'PIN Atual' : 'Current PIN'}
                </label>
                <input
                  type="password"
                  value={currentPinInput}
                  onChange={(e) => setCurrentPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  className="w-full px-4 py-3 text-center text-xl tracking-widest border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength={6}
                  data-testid="current-pin-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Novo PIN' : 'New PIN'}
                </label>
                <input
                  type="password"
                  value={newPinInput}
                  onChange={(e) => setNewPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  className="w-full px-4 py-3 text-center text-xl tracking-widest border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength={6}
                  data-testid="new-pin-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Confirmar Novo PIN' : 'Confirm New PIN'}
                </label>
                <input
                  type="password"
                  value={confirmPinInput}
                  onChange={(e) => setConfirmPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={(e) => e.key === 'Enter' && handleChangePinSubmit()}
                  placeholder="••••••"
                  className="w-full px-4 py-3 text-center text-xl tracking-widest border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength={6}
                  data-testid="confirm-pin-input"
                />
              </div>
              
              {pinChangeError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {pinChangeError}
                </div>
              )}
              
              {pinChangeSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm flex items-center gap-2">
                  <CheckCircle size={16} />
                  {pinChangeSuccess}
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowChangePinModal(false);
                    setCurrentPinInput('');
                    setNewPinInput('');
                    setConfirmPinInput('');
                    setPinChangeError('');
                    setPinChangeSuccess('');
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  data-testid="cancel-pin-change"
                >
                  {language === 'pt' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={handleChangePinSubmit}
                  disabled={changingPin}
                  className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="submit-pin-change"
                >
                  {changingPin ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {language === 'pt' ? 'A guardar...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <Shield size={16} />
                      {language === 'pt' ? 'Alterar PIN' : 'Change PIN'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="expense-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-heading font-semibold text-gray-900 flex items-center gap-2">
                <Briefcase size={24} className="text-green-500" />
                {editingExpense 
                  ? (language === 'pt' ? 'Editar Custo' : 'Edit Expense')
                  : (language === 'pt' ? 'Adicionar Custo' : 'Add Expense')
                }
              </h3>
              <button onClick={closeExpenseModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Descrição' : 'Description'} *
                </label>
                <input
                  type="text"
                  value={expenseForm.name}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={language === 'pt' ? 'Ex: Produtos de limpeza' : 'Ex: Cleaning supplies'}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  data-testid="expense-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'pt' ? 'Valor (€)' : 'Amount (€)'} *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    data-testid="expense-amount-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'pt' ? 'Data' : 'Date'}
                  </label>
                  <input
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    data-testid="expense-date-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'pt' ? 'Tipo' : 'Type'}
                  </label>
                  <select
                    value={expenseForm.expense_type}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, expense_type: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    data-testid="expense-type-select"
                  >
                    <option value="fixed">{language === 'pt' ? 'Fixo (mensal)' : 'Fixed (monthly)'}</option>
                    <option value="service">{language === 'pt' ? 'Por serviço' : 'Per service'}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'pt' ? 'Categoria' : 'Category'}
                  </label>
                  <select
                    value={expenseForm.category}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    data-testid="expense-category-select"
                  >
                    <option value="supplies">{language === 'pt' ? 'Produtos' : 'Supplies'}</option>
                    <option value="transport">{language === 'pt' ? 'Transporte' : 'Transport'}</option>
                    <option value="equipment">{language === 'pt' ? 'Equipamento' : 'Equipment'}</option>
                    <option value="rent">{language === 'pt' ? 'Renda' : 'Rent'}</option>
                    <option value="other">{language === 'pt' ? 'Outro' : 'Other'}</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Notas' : 'Notes'}
                </label>
                <textarea
                  value={expenseForm.notes}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder={language === 'pt' ? 'Detalhes adicionais...' : 'Additional details...'}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  rows={2}
                  data-testid="expense-notes-input"
                />
              </div>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={expenseForm.recurring}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, recurring: e.target.checked }))}
                  className="w-4 h-4 rounded text-green-500 focus:ring-green-500"
                />
                <span className="text-sm text-gray-700">
                  {language === 'pt' ? 'Custo recorrente (mensal)' : 'Recurring cost (monthly)'}
                </span>
              </label>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={closeExpenseModal}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  {language === 'pt' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={handleSaveExpense}
                  disabled={savingExpense || !expenseForm.name || !expenseForm.amount}
                  className="flex-1 px-4 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="save-expense-btn"
                >
                  {savingExpense ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {language === 'pt' ? 'A guardar...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      {language === 'pt' ? 'Guardar' : 'Save'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Service Finance Modal */}
      {showEditServiceModal && editingService && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-service-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-heading font-semibold text-gray-900 flex items-center gap-2">
                <DollarSign size={24} className="text-amber-500" />
                {language === 'pt' ? 'Editar Valores do Serviço' : 'Edit Service Values'}
              </h3>
              <button onClick={closeEditServiceModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={24} />
              </button>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-600">
                <strong>{editingService.service_type || 'Limpeza'}</strong> - {editingService.name}
              </p>
              <p className="text-xs text-gray-500">
                {new Date(editingService.status_updated_at || editingService.updated_at).toLocaleDateString('pt-PT')}
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Preço Cobrado (€)' : 'Price Charged (€)'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={serviceFinanceForm.estimated_price}
                  onChange={(e) => setServiceFinanceForm(prev => ({ ...prev, estimated_price: e.target.value }))}
                  placeholder={editingService.revenue?.toFixed(2) || '0.00'}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  data-testid="service-price-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {language === 'pt' ? 'Valor atual:' : 'Current value:'} €{editingService.revenue?.toFixed(2) || '0.00'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Custo Staff (€)' : 'Staff Cost (€)'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={serviceFinanceForm.staff_cost}
                  onChange={(e) => setServiceFinanceForm(prev => ({ ...prev, staff_cost: e.target.value }))}
                  placeholder={editingService.staffCost?.toFixed(2) || '0.00'}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  data-testid="service-staff-cost-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {language === 'pt' ? 'Valor calculado:' : 'Calculated value:'} €{editingService.staffCost?.toFixed(2) || '0.00'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Notas' : 'Notes'}
                </label>
                <textarea
                  value={serviceFinanceForm.notes}
                  onChange={(e) => setServiceFinanceForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder={language === 'pt' ? 'Notas sobre ajustes...' : 'Notes about adjustments...'}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  rows={2}
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={closeEditServiceModal}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  {language === 'pt' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={handleSaveServiceFinance}
                  disabled={savingServiceFinance}
                  className="flex-1 px-4 py-3 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="save-service-finance-btn"
                >
                  {savingServiceFinance ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {language === 'pt' ? 'A guardar...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      {language === 'pt' ? 'Guardar' : 'Save'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="delete-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                {language === 'pt' ? 'Confirmar Exclusão' : 'Confirm Delete'}
              </h3>
              <p className="text-gray-600 mb-6">
                {language === 'pt' 
                  ? `Tem certeza que deseja excluir o ${deleteModal.type === 'contact' ? 'pedido de orçamento' : 'agendamento'} de "${deleteModal.name}"? Esta ação não pode ser desfeita.`
                  : `Are you sure you want to delete the ${deleteModal.type === 'contact' ? 'quote request' : 'appointment'} from "${deleteModal.name}"? This action cannot be undone.`
                }
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={closeDeleteModal}
                  className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  data-testid="cancel-delete"
                >
                  {language === 'pt' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
                  data-testid="confirm-delete"
                >
                  <Trash2 size={16} />
                  {language === 'pt' ? 'Excluir' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {imageModal.show && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeImageModal}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full">
            {/* Close button */}
            <button
              onClick={closeImageModal}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors flex items-center gap-2"
              data-testid="close-image-modal"
            >
              <X size={24} />
              <span className="text-sm">{language === 'pt' ? 'Fechar' : 'Close'}</span>
            </button>
            
            {/* Title */}
            <p className="absolute -top-12 left-0 text-white text-sm">{imageModal.title}</p>
            
            {/* Image */}
            <img
              src={imageModal.url}
              alt={imageModal.title}
              className="w-full h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Open in new tab button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(imageModal.url, '_blank');
              }}
              className="absolute bottom-4 right-4 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <ExternalLink size={16} />
              {language === 'pt' ? 'Abrir em nova aba' : 'Open in new tab'}
            </button>
          </div>
        </div>
      )}

      {/* New/Edit Appointment Modal */}
      {showNewAppointmentModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl sm:my-8 max-h-[96vh] sm:max-h-none flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className={`bg-gradient-to-r ${editingAppointment ? 'from-amber-500 to-orange-500' : 'from-blue-600 to-blue-700'} px-6 py-4 rounded-t-2xl flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  {editingAppointment ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                  ) : (
                    <Plus size={24} className="text-white" />
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    {editingAppointment 
                      ? (language === 'pt' ? 'Editar Agendamento' : 'Edit Appointment')
                      : (language === 'pt' ? 'Novo Agendamento' : 'New Appointment')}
                  </h3>
                  <p className="text-white/80 text-sm">
                    {editingAppointment 
                      ? editingAppointment.name 
                      : (language === 'pt' ? 'Cliente via WhatsApp' : 'WhatsApp client')}
                  </p>
                </div>
              </div>
              <button
                onClick={closeAppointmentModal}
                className="text-white/80 hover:text-white p-2"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Form */}
            <div className="p-4 sm:p-6 space-y-4 flex-1 min-h-0 overflow-y-auto sm:max-h-[60vh]">
              {!editingAppointment && (
                <ClientSearch
                  token={token}
                  language={language}
                  onSelect={(c) => setNewAppointment(prev => ({
                    ...prev,
                    name: c.name || prev.name,
                    phone: c.phone || prev.phone,
                    email: c.email && c.email !== 'sem-email@sancur.ie' ? c.email : prev.email,
                    address: c.address || prev.address,
                    eircode: c.eircode || prev.eircode,
                    property_type: c.property_type || prev.property_type,
                    bedrooms: c.bedrooms ?? prev.bedrooms,
                    bathrooms: c.bathrooms ?? prev.bathrooms,
                    living_rooms: c.living_rooms ?? prev.living_rooms,
                    has_kitchen: c.has_kitchen ?? prev.has_kitchen,
                    has_pets: c.has_pets ?? prev.has_pets,
                    ...(c.is_airbnb ? { service_type: 'airbnb', airbnb_price: prev.airbnb_price || String(airbnbSettings.price), preferred_time: airbnbSettings.time } : {})
                  }))}
                />
              )}
              {/* Client Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'pt' ? 'Nome do Cliente *' : 'Client Name *'}
                  </label>
                  <input
                    type="text"
                    value={newAppointment.name}
                    onChange={e => setNewAppointment(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="João Silva"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'pt' ? 'Telefone *' : 'Phone *'}
                  </label>
                  <input
                    type="tel"
                    value={newAppointment.phone}
                    onChange={e => setNewAppointment(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+353 83 123 4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newAppointment.email}
                    onChange={e => setNewAppointment(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="joao@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Eircode
                  </label>
                  <input
                    type="text"
                    value={newAppointment.eircode}
                    onChange={e => setNewAppointment(prev => ({ ...prev, eircode: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="D01 AB12"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Morada' : 'Address'}
                </label>
                <input
                  type="text"
                  value={newAppointment.address}
                  onChange={e => setNewAppointment(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="123 Main Street, Dublin"
                />
              </div>
              
              {/* Service Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'pt' ? 'Tipo de Serviço' : 'Service Type'}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { id: 'regular', label: language === 'pt' ? 'Regular' : 'Regular', icon: '🧹' },
                    { id: 'deep', label: language === 'pt' ? 'Deep Clean' : 'Deep Clean', icon: '✨' },
                    { id: 'office', label: language === 'pt' ? 'Escritório' : 'Office', icon: '🏢' },
                    { id: 'endoftenancy', label: language === 'pt' ? 'Fim Arrend.' : 'End Tenancy', icon: '🏠' },
                    { id: 'upholstery', label: language === 'pt' ? 'Estofados' : 'Upholstery', icon: '🛋️' },
                    { id: 'airbnb', label: 'Airbnb', icon: '🏡' },
                    { id: 'custom', label: language === 'pt' ? 'Outro' : 'Other', icon: '➕' }
                  ].map(service => (
                    <button
                      key={service.id}
                      type="button"
                      data-testid={`service-type-${service.id}`}
                      onClick={() => setNewAppointment(prev => ({
                        ...prev,
                        service_type: service.id,
                        ...(service.id === 'airbnb' && !prev.airbnb_price ? { airbnb_price: String(airbnbSettings.price), preferred_time: airbnbSettings.time } : {})
                      }))}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        newAppointment.service_type === service.id
                          ? service.id === 'custom' 
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : service.id === 'airbnb'
                              ? 'border-rose-500 bg-rose-50 text-rose-700'
                              : 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <span className="text-lg mr-1">{service.icon}</span>
                      {service.label}
                    </button>
                  ))}
                </div>
                
                {/* Custom Service Fields */}
                {newAppointment.service_type === 'custom' && (
                  <div className="mt-3 p-4 bg-green-50 rounded-xl border border-green-200 space-y-3">
                    <div className="flex items-center gap-2 text-green-700 font-medium text-sm">
                      <Plus size={16} />
                      {language === 'pt' ? 'Serviço Personalizado' : 'Custom Service'}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          {language === 'pt' ? 'Nome do Serviço *' : 'Service Name *'}
                        </label>
                        <input
                          type="text"
                          value={newAppointment.custom_service_name}
                          onChange={e => setNewAppointment(prev => ({ ...prev, custom_service_name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                          placeholder={language === 'pt' ? 'Ex: Limpeza de Vidros' : 'Ex: Window Cleaning'}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          {language === 'pt' ? 'Preço (€) *' : 'Price (€) *'}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newAppointment.custom_service_price}
                          onChange={e => setNewAppointment(prev => ({ ...prev, custom_service_price: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Airbnb Fields */}
                {newAppointment.service_type === 'airbnb' && (
                  <div className="mt-3 p-4 bg-rose-50 rounded-xl border border-rose-200 space-y-3" data-testid="airbnb-fields">
                    <div className="flex items-center justify-between text-rose-700 font-medium text-sm">
                      <span>🏡 {language === 'pt' ? 'Limpeza Airbnb — preço fixo, hora fixa' : 'Airbnb Cleaning — fixed price, fixed time'}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{language === 'pt' ? 'Preço por limpeza (€) *' : 'Price per cleaning (€) *'}</label>
                        <div className="flex gap-2">
                          <input
                            type="number" step="0.01" min="0"
                            value={newAppointment.airbnb_price}
                            onChange={e => setNewAppointment(prev => ({ ...prev, airbnb_price: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-sm"
                            data-testid="airbnb-price-input"
                          />
                          {!editingAppointment && (
                            <button type="button" onClick={saveAirbnbDefaults} data-testid="airbnb-save-default"
                              className="whitespace-nowrap text-xs px-3 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-100">
                              {language === 'pt' ? 'Guardar padrão' : 'Save default'}
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{language === 'pt' ? `Padrão atual: €${airbnbSettings.price}` : `Current default: €${airbnbSettings.price}`}</p>
                      </div>
                      {!editingAppointment && newAppointment.airbnb_mode === 'weekdays' && (
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">{language === 'pt' ? 'Durante quantas semanas *' : 'For how many weeks *'}</label>
                          <input
                            type="number" min="1" max="52"
                            value={newAppointment.airbnb_weeks}
                            onChange={e => setNewAppointment(prev => ({ ...prev, airbnb_weeks: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-sm"
                            data-testid="airbnb-weeks-input"
                          />
                        </div>
                      )}
                    </div>
                    {!editingAppointment && (
                      <div className="flex gap-1 text-xs" data-testid="airbnb-mode-toggle">
                        {[{ id: 'weekdays', label: language === 'pt' ? 'Dias da semana (repetir)' : 'Weekdays (repeat)' }, { id: 'dates', label: language === 'pt' ? 'Datas específicas' : 'Specific dates' }].map(mo => (
                          <button key={mo.id} type="button" data-testid={`airbnb-mode-${mo.id}`}
                            onClick={() => setNewAppointment(prev => ({ ...prev, airbnb_mode: mo.id }))}
                            className={`px-3 py-1.5 rounded-full border-2 transition-colors ${newAppointment.airbnb_mode === mo.id ? 'bg-rose-500 border-rose-500 text-white' : 'border-gray-200 text-gray-600 hover:border-rose-300'}`}>
                            {mo.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {!editingAppointment && newAppointment.airbnb_mode === 'dates' && (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{language === 'pt' ? 'Escolha as datas *' : 'Pick the dates *'}</label>
                        <MultiDatePicker language={language} value={newAppointment.airbnb_dates} onChange={(dates) => setNewAppointment(prev => ({ ...prev, airbnb_dates: dates }))} />
                        <p className="text-xs text-gray-500 mt-2" data-testid="airbnb-dates-summary">
                          {newAppointment.airbnb_dates.length} {language === 'pt' ? 'limpeza(s) selecionada(s)' : 'cleaning(s) selected'}
                        </p>
                      </div>
                    )}
                    {!editingAppointment && newAppointment.airbnb_mode === 'weekdays' && (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{language === 'pt' ? 'Dias da semana *' : 'Weekdays *'}</label>
                        <div className="flex flex-wrap gap-2">
                          {(language === 'pt' ? ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']).map((d, i) => {
                            const active = newAppointment.airbnb_weekdays.includes(i);
                            return (
                              <button key={d} type="button" data-testid={`airbnb-weekday-${i}`}
                                onClick={() => setNewAppointment(prev => ({ ...prev, airbnb_weekdays: active ? prev.airbnb_weekdays.filter(x => x !== i) : [...prev.airbnb_weekdays, i].sort() }))}
                                className={`px-3 py-1.5 rounded-full text-sm border-2 transition-colors ${active ? 'bg-rose-500 border-rose-500 text-white' : 'border-gray-200 text-gray-600 hover:border-rose-300'}`}>
                                {d}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-xs text-gray-500 mt-2" data-testid="airbnb-summary">
                          {language === 'pt'
                            ? `${newAppointment.airbnb_weekdays.length} dia(s)/semana × ${parseInt(newAppointment.airbnb_weeks) || 0} semanas = ${newAppointment.airbnb_weekdays.length * (parseInt(newAppointment.airbnb_weeks) || 0)} limpezas a partir da data de início`
                            : `${newAppointment.airbnb_weekdays.length} day(s)/week × ${parseInt(newAppointment.airbnb_weeks) || 0} weeks = ${newAppointment.airbnb_weekdays.length * (parseInt(newAppointment.airbnb_weeks) || 0)} cleanings from the start date`}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Property Details */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Home size={16} />
                  {language === 'pt' ? 'Detalhes do Imóvel' : 'Property Details'}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <BedDouble size={12} />
                      {language === 'pt' ? 'Quartos' : 'Bedrooms'}
                    </label>
                    <select
                      value={newAppointment.bedrooms}
                      onChange={e => setNewAppointment(prev => ({ ...prev, bedrooms: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      {[0,1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Bath size={12} />
                      {language === 'pt' ? 'WC' : 'Bathrooms'}
                    </label>
                    <select
                      value={newAppointment.bathrooms}
                      onChange={e => setNewAppointment(prev => ({ ...prev, bathrooms: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      {[0,1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Sofa size={12} />
                      {language === 'pt' ? 'Salas' : 'Living Rooms'}
                    </label>
                    <select
                      value={newAppointment.living_rooms}
                      onChange={e => setNewAppointment(prev => ({ ...prev, living_rooms: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      {[0,1,2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {language === 'pt' ? 'Tipo' : 'Type'}
                    </label>
                    <select
                      value={newAppointment.property_type}
                      onChange={e => setNewAppointment(prev => ({ ...prev, property_type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="apartment">{language === 'pt' ? 'Apartamento' : 'Apartment'}</option>
                      <option value="house">{language === 'pt' ? 'Casa' : 'House'}</option>
                      <option value="studio">Studio</option>
                      <option value="office">{language === 'pt' ? 'Escritório' : 'Office'}</option>
                    </select>
                  </div>
                </div>
                
                {/* Toggles */}
                <div className="flex flex-wrap gap-4 mt-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newAppointment.has_kitchen}
                      onChange={e => setNewAppointment(prev => ({ ...prev, has_kitchen: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <ChefHat size={16} className="text-gray-500" />
                    <span className="text-sm">{language === 'pt' ? 'Cozinha' : 'Kitchen'}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newAppointment.has_pets}
                      onChange={e => setNewAppointment(prev => ({ ...prev, has_pets: e.target.checked }))}
                      className="w-4 h-4 text-amber-600 rounded"
                    />
                    <PawPrint size={16} className="text-amber-500" />
                    <span className="text-sm">{language === 'pt' ? 'Animais' : 'Pets'}</span>
                  </label>
                </div>
              </div>
              
              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className={newAppointment.service_type === 'airbnb' && !editingAppointment && newAppointment.airbnb_mode === 'dates' ? 'hidden' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {newAppointment.service_type === 'airbnb' && !editingAppointment
                      ? (language === 'pt' ? 'Data de início *' : 'Start date *')
                      : (language === 'pt' ? 'Data *' : 'Date *')}
                  </label>
                  <input
                    type="date"
                    data-testid="appointment-date-input"
                    value={newAppointment.preferred_date}
                    onChange={e => setNewAppointment(prev => ({ ...prev, preferred_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'pt' ? 'Hora' : 'Time'}
                  </label>
                  <select
                    value={newAppointment.preferred_time}
                    onChange={e => setNewAppointment(prev => ({ ...prev, preferred_time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {['08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', 
                      '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
                      '16:00', '16:30', '17:00', '17:30', '18:00'].map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Special Requests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Pedidos Especiais' : 'Special Requests'}
                </label>
                <textarea
                  value={newAppointment.special_requests}
                  onChange={e => setNewAppointment(prev => ({ ...prev, special_requests: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={language === 'pt' ? 'Ex: Focar na cozinha, usar produtos específicos...' : 'E.g., Focus on kitchen, use specific products...'}
                />
              </div>
            </div>
            
            {/* Estimate Preview & Actions */}
            <div className="border-t bg-gradient-to-r from-green-50 to-emerald-50 px-4 sm:px-6 py-3 sm:py-4 sm:rounded-b-2xl flex-shrink-0">
              {/* Discount */}
              <div className="bg-white rounded-xl p-4 mb-4 border border-amber-200" data-testid="discount-section">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-amber-700">🏷️ {language === 'pt' ? 'Desconto' : 'Discount'}</span>
                  <div className="flex gap-1">
                    {[
                      { id: 'none', label: language === 'pt' ? 'Sem' : 'None' },
                      { id: 'percent', label: '%' },
                      { id: 'fixed', label: '€' }
                    ].map(opt => (
                      <button key={opt.id} type="button" data-testid={`discount-type-${opt.id}`}
                        onClick={() => setNewAppointment(prev => ({ ...prev, discount_type: opt.id, discount_value: opt.id === 'none' ? '' : prev.discount_value }))}
                        className={`px-3 py-1.5 rounded-full text-sm border-2 transition-colors ${newAppointment.discount_type === opt.id ? 'bg-amber-500 border-amber-500 text-white' : 'border-gray-200 text-gray-600 hover:border-amber-300'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {newAppointment.discount_type !== 'none' && (
                    <input
                      type="number" min="0" step="0.01"
                      value={newAppointment.discount_value}
                      onChange={e => setNewAppointment(prev => ({ ...prev, discount_value: e.target.value }))}
                      placeholder={newAppointment.discount_type === 'percent' ? '10' : '15.00'}
                      className="w-28 px-3 py-1.5 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                      data-testid="discount-value-input"
                    />
                  )}
                  {appointmentEstimate.discount > 0 && (
                    <span className="text-sm text-amber-700 font-semibold" data-testid="discount-amount">
                      −€{appointmentEstimate.discount.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
              {/* Estimate */}
              <div className="bg-white rounded-xl p-4 mb-4 border-2 border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">
                      {newAppointment.service_type === 'airbnb' || newAppointment.service_type === 'custom'
                        ? (language === 'pt' ? 'Preço Final' : 'Final Price')
                        : (language === 'pt' ? 'Estimativa Automática' : 'Automatic Estimate')}
                    </p>
                    <div className="flex items-center gap-4 mt-1">
                      {appointmentEstimate.discount > 0 && (
                        <span className="text-xl text-gray-400 line-through" data-testid="original-price">€{appointmentEstimate.originalPrice}</span>
                      )}
                      <span className="text-3xl font-bold text-green-700" data-testid="final-price">€{appointmentEstimate.price}</span>
                      {appointmentEstimate.time && appointmentEstimate.time !== 'Airbnb' && appointmentEstimate.time !== 'Custom' && (
                        <>
                          <span className="text-lg text-gray-600">|</span>
                          <span className="text-xl font-semibold text-blue-600">{appointmentEstimate.time}</span>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {newAppointment.service_type === 'airbnb'
                        ? (language === 'pt' ? 'Preço fixo por limpeza Airbnb' : 'Fixed price per Airbnb cleaning')
                        : newAppointment.service_type === 'custom'
                          ? (language === 'pt' ? 'Preço definido manualmente' : 'Manually set price')
                          : newAppointment.service_type === 'deep' || newAppointment.service_type === 'endoftenancy'
                            ? '€31/hora (Deep Clean)'
                            : '€23/hora (Regular)'}
                    </p>
                  </div>
                  <div className="text-5xl">💰</div>
                </div>
              </div>
              
              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={closeAppointmentModal}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  {language === 'pt' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={editingAppointment ? handleUpdateAppointment : (newAppointment.service_type === 'airbnb' ? handleCreateAirbnbBatch : handleCreateManualAppointment)}
                  disabled={creatingAppointment}
                  className={`flex-1 px-4 py-3 ${editingAppointment ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50`}
                  data-testid="confirm-create-appointment"
                >
                  {creatingAppointment ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {editingAppointment 
                        ? (language === 'pt' ? 'Guardando...' : 'Saving...')
                        : (language === 'pt' ? 'Criando...' : 'Creating...')}
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      {editingAppointment 
                        ? (language === 'pt' ? 'Guardar Alterações' : 'Save Changes')
                        : newAppointment.service_type === 'airbnb'
                          ? (language === 'pt' ? `Agendar ${newAppointment.airbnb_mode === 'dates' ? newAppointment.airbnb_dates.length : newAppointment.airbnb_weekdays.length * (parseInt(newAppointment.airbnb_weeks) || 0)} Limpezas` : `Schedule ${newAppointment.airbnb_mode === 'dates' ? newAppointment.airbnb_dates.length : newAppointment.airbnb_weekdays.length * (parseInt(newAppointment.airbnb_weeks) || 0)} Cleanings`)
                          : (language === 'pt' ? 'Confirmar Agendamento' : 'Confirm Appointment')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {editingCompleted && (
        <CompletedServiceEditModal appointment={editingCompleted} staff={staff} hours={getJobWorkedHours(editingCompleted.id).hours} token={token} language={language}
          onClose={() => setEditingCompleted(null)} onSaved={() => fetchAllData(true)} />
      )}
      <ClientProfileModal
        client={profileClient}
        token={token}
        language={language}
        onClose={() => setProfileClient(null)}
        onNewAppointment={(p) => {
          setProfileClient(null);
          setEditingAppointment(null);
          setNewAppointment(prev => ({
            ...prev, name: p.name || '', phone: p.phone || '', email: p.email && p.email !== 'sem-email@sancur.ie' ? p.email : '',
            address: p.address || '', eircode: p.eircode || '', property_type: p.property_type || prev.property_type,
            bedrooms: p.bedrooms ?? prev.bedrooms, bathrooms: p.bathrooms ?? prev.bathrooms, living_rooms: p.living_rooms ?? prev.living_rooms, has_pets: p.has_pets ?? prev.has_pets,
            ...(p.is_airbnb ? { service_type: 'airbnb', airbnb_price: prev.airbnb_price || String(airbnbSettings.price), preferred_time: airbnbSettings.time } : {})
          }));
          setShowNewAppointmentModal(true);
        }}
      />
    </div>
  );
};

export default AdminDashboard;
